/**
 * Wire types for the demo API.
 *
 * Verified against src/EasyFarm.Api/Demo/{DemoContracts,DemoEndpoints}.cs, which landed
 * in this repo concurrently with this client (confirmed present as of this writing —
 * see web/README.md "What could not be verified" for what is still unconfirmed, e.g.
 * actual JSON casing behavior has never been observed over the wire).
 *
 * Two things worth flagging because they are easy to get wrong by reasoning about
 * System.Text.Json defaults instead of reading the actual contract:
 *
 *   - Enums use `[JsonConverter(typeof(JsonStringEnumConverter))]` with NO naming
 *     policy argument, which serializes the C# member name AS WRITTEN —
 *     PascalCase ("Income", "Card", "Receipt"), not the lowercase values the
 *     Postgres CHECK constraints use ("income", "card", "receipt"). Do not
 *     "fix" these back to lowercase without re-checking DemoContracts.cs.
 *   - `VendorResponse` / `AccountResponse` / `EntryResponse` do NOT carry
 *     `tenantId` — RLS scopes the query, so the API never echoes it back.
 */

export type Guid = string;

export type AccountKind = "Income" | "Expense" | "Asset" | "Liability" | "Equity";

export type PaymentKind = "Card" | "Check" | "Cash" | "Ach";

/**
 * `Livestock` (ef-041-4) is a purchase or sale posted by
 * `POST /api/demo/livestock/{purchase,sale}` — never settable from `CreateEntryRequest`
 * (see its own `source` field below, which stays `Manual | Receipt`).
 * `Payroll` and `Register` (ef-042-7) are likewise never settable from
 * `CreateEntryRequest` — both are hand-posted server-side (pay-run post, register write
 * path) and only ever arrive on a read.
 * `Crop` (ef-045-5) is the same shape as `Livestock` — a crop sale posted by
 * `POST /api/demo/farm/field-crops/{id}/harvest` (`FieldHarvestEndpoints.cs`), never
 * settable from `CreateEntryRequest` either. Widened here so `GET /entries` reads a
 * `source: 'crop'` row back without throwing — the exact class of bug
 * `ef-041-6-contract.md` names for `Livestock` (see `fields-harvest.spec.ts`'s own header).
 */
export type EntrySource = "Manual" | "Receipt" | "Livestock" | "Payroll" | "Register" | "Crop";

export interface Vendor {
  id: Guid;
  name: string;
}

/** `POST /api/demo/vendors` (ef-042-3). */
export interface CreateVendorRequest {
  name: string;
}

/** `PATCH /api/demo/vendors/{id}` (ef-042-3). `name` is this entity's only field, so an
 *  omitted body is a no-op update server-side rather than a validation failure. */
export interface UpdateVendorRequest {
  name?: string;
}

export interface Account {
  id: Guid;
  number: string;
  name: string;
  kind: AccountKind;
  /** ef-044-2 — null when this account has no Schedule F line mapped yet
   *  (`account.schedule_f_line`, db/migrations/0055). */
  scheduleFLine: string | null;
  /** ef-044-4 — null = active. See db/migrations/0057_account_archive.sql. */
  archivedAt: string | null;
}

export interface PaymentMethod {
  id: Guid;
  label: string;
  kind: PaymentKind;
}

export interface PaymentAnomaly {
  attemptedPaymentMethodId: Guid;
  previouslyUsed: PaymentMethod[];
}

export interface Entry {
  /**
   * The receipt photograph kept against this entry, if any. ef-006.
   *
   * An id, not the image — fetch it from `/api/demo/receipts/{id}`, which is
   * session-authenticated. Absent means no photograph was kept, which is a different
   * claim from "the scan failed": a hand-typed entry never had one.
   */
  receiptImageId?: string | null;

  id: Guid;
  entityId: Guid;
  vendorId: Guid | null;
  accountId: Guid | null;
  paymentMethodId: Guid | null;
  /** Display-only. Never add, subtract, or otherwise compute with this client-side. */
  amount: number;
  memo: string | null;
  occurredOn: string; // "YYYY-MM-DD"
  source: EntrySource;
  createdAt: string; // ISO 8601 timestamp
}

/**
 * One row of GET /api/demo/books/activity — Books' Activity tab, ef-042-0a. The real,
 * uncurated ledger feed: every entry regardless of account kind, unlike `Entry` above.
 *
 * `source` is a plain `string`, deliberately NOT `EntrySource` — the server accepts a
 * fourth value (`Payroll`) this shared union has never had a member for (see
 * BooksActivityEndpoints.cs's own remarks), and this type must not crash or silently
 * drop a row this API can genuinely return.
 */
export interface BooksActivityEntry {
  id: Guid;
  entityId: Guid;
  vendorId: Guid | null;
  accountId: Guid | null;
  paymentMethodId: Guid | null;
  amount: number;
  memo: string | null;
  occurredOn: string; // "YYYY-MM-DD"
  source: string;
  createdAt: string; // ISO 8601 timestamp
  receiptImageId?: string | null;
}

/**
 * One `document` row — an invoice (AR, income the farm is owed) or a bill (AP, expense the
 * farm owes). ef-043-4, `GET`/`POST /api/demo/books/documents`. `outstanding` is derived
 * server-side (amount less what has been settled) and never stored — see
 * DocumentContracts.cs's own remarks. No settlement route exists yet (ef-043-5), so
 * `outstanding` equals `amount` for every document today.
 */
export interface Document {
  id: Guid;
  entityId: Guid;
  kind: "invoice" | "bill";
  counterpartyId: Guid | null;
  accountId: Guid;
  amount: number;
  outstanding: number;
  incurredOn: string; // "YYYY-MM-DD"
  dueOn: string | null; // "YYYY-MM-DD"
  memo: string | null;
  createdAt: string; // ISO 8601 timestamp
}

/**
 * Response of GET /api/demo/books/documents/aging (ef-049-0e) — the three sums the
 * vendors screen's own aging strip reads, computed server-side and unbounded by the
 * 200-row `getDocuments({ limit: 200 })` cap the document list still uses.
 */
export interface DocumentAging {
  arTotal: number;
  apTotal: number;
  overdueTotal: number;
}

/** Body for POST /api/demo/books/documents (ef-043-4). `kind` is validated against
 *  `accountId`'s own account kind server-side — a mismatch is a 400 naming `accountId`. */
export interface CreateDocumentRequest {
  kind: "invoice" | "bill";
  accountId: Guid;
  amount: number;
  incurredOn: string; // "YYYY-MM-DD"
  dueOn?: string; // "YYYY-MM-DD"
  counterpartyId?: Guid;
  memo?: string;
}

/** Body for POST /api/demo/books/documents/{id}/settle (ef-043-5). Partial settlement is
 *  allowed — `amount` need not equal the document's own outstanding balance, only be no
 *  more than it (409 otherwise). */
export interface SettleDocumentRequest {
  cashAccountId: Guid;
  amount: number;
}

/** Response of a successful POST .../settle. */
export interface SettleDocumentResponse {
  documentId: Guid;
  entryId: Guid;
  amountSettled: number;
  outstanding: number;
}

/**
 * Response of POST /api/demo/books/reconcile/{accountId}/import — ef-043-1. The four
 * counts always satisfy `rowsParsed === rowsImported + rowsSkipped + rowsDuplicate` (see
 * BooksReconcileContracts.cs's own remarks).
 */
export interface ImportStatementResult {
  rowsParsed: number;
  rowsImported: number;
  rowsSkipped: number;
  rowsDuplicate: number;
  /** ef-058 anchor — which parser read the body. A saved column mapping answers "csv". */
  format?: "csv" | "ofx";
}

// ---- ef-058 anchors: saved CSV mapping, auto-match, add-to-books ---------------------

/** `PUT /api/demo/books/reconcile/{accountId}/mapping` — ef-058-6. Columns are named by
 *  header text (case-insensitive), never by position. Exactly one of `amountColumn` or the
 *  `debitColumn`+`creditColumn` pair. */
export interface BankImportMappingRequest {
  dateColumn: string;
  descriptionColumn: string;
  amountColumn?: string | null;
  debitColumn?: string | null;
  creditColumn?: string | null;
  dateFormat: "iso" | "us" | "eu";
  /** True when the bank exports withdrawals as positive numbers. */
  negateAmount: boolean;
}

/** Response of GET|PUT `.../mapping`. GET answers 204 (no body) when none is saved. */
export interface BankImportMapping extends BankImportMappingRequest {
  accountId: Guid;
}

/** One pair `POST .../auto-match` cleared. `reconciledOn` is the STATEMENT line's date. */
export interface AutoMatchedPair {
  statementLineId: Guid;
  entryLineId: Guid;
  reconciledOn: string; // "YYYY-MM-DD"
}

/** Response of `POST /api/demo/books/reconcile/{accountId}/auto-match` — ef-058-7.
 *  `matched + ambiguous + noCandidate` = the unmatched statement lines the call looked at. */
export interface AutoMatchResult {
  matched: number;
  ambiguous: number;
  noCandidate: number;
  matches: AutoMatchedPair[];
}

/** Body of `POST /api/demo/books/reconcile/{accountId}/add-entry` — ef-058-8. */
export interface AddStatementEntryRequest {
  statementLineId: Guid;
  /** Required server-side; a missing value is a 400 on `categoryAccountId`. */
  categoryAccountId?: Guid | null;
  /** Defaults to the statement line's description. 200 characters at most. */
  memo?: string | null;
  vendorId?: Guid | null;
}

/** Response of `POST .../add-entry` (201). `entryLineId` is the bank account's own leg. */
export interface AddStatementEntryResult {
  statementLineId: Guid;
  entryId: Guid;
  entryLineId: Guid;
  reconciledOn: string; // "YYYY-MM-DD"
}

/**
 * One row of GET /api/demo/books/reconcile/lines — ef-043-1, an imported bank-statement
 * line, before any reconciliation to the books. `amount` is signed exactly as the bank
 * exported it — see db/migrations/0049's own remarks.
 */
export interface BankStatementLine {
  id: Guid;
  accountId: Guid;
  postedOn: string; // "YYYY-MM-DD"
  amount: number;
  description: string;
  importBatchId: Guid;
  importedAt: string; // ISO 8601 timestamp
}

/**
 * One row of GET /api/demo/books/reconcile/{accountId}/unmatched — ef-043-2, a statement
 * line with no matched entry_line yet. See BooksReconcileContracts.cs's own
 * UnmatchedStatementLineResponse.
 */
export interface UnmatchedStatementLine {
  id: Guid;
  accountId: Guid;
  occurredOn: string; // "YYYY-MM-DD"
  amount: number;
  description: string;
}

/**
 * One row of GET /api/demo/books/reconcile/{accountId}/candidates — ef-043-2, an
 * unreconciled entry_line eligible to be matched against a given statement line.
 * `entryLineId` is the id a POST .../match call names. `amount` is this account's own
 * SIGNED contribution (debit increases, credit decreases — ADR 0001), matching a
 * statement line's own signed `amount` exactly when this is a real candidate.
 */
export interface ReconcileCandidate {
  entryLineId: Guid;
  entryId: Guid;
  occurredOn: string; // "YYYY-MM-DD"
  amount: number;
  memo?: string | null;
}

/** Body for POST /api/demo/books/reconcile/{accountId}/match — ef-043-2. */
export interface MatchStatementLineRequest {
  statementLineId: Guid;
  entryLineId: Guid;
}

/**
 * Response for POST /api/demo/books/reconcile/{accountId}/match — ef-043-2.
 * `reconciledOn` is the STATEMENT line's own date, not the entry line's.
 */
export interface MatchStatementLineResult {
  statementLineId: Guid;
  entryLineId: Guid;
  reconciledOn: string; // "YYYY-MM-DD"
}

/** Body for POST /api/demo/books/reconcile/{accountId}/unmatch — ef-043-2. A 409 means
 * the matched entry falls in a locked period — refused, same reasoning
 * BooksReconcileEndpoints.cs's own remarks give. */
export interface UnmatchStatementLineRequest {
  statementLineId: Guid;
}

/**
 * Response of GET /api/demo/books/reconcile/{accountId}/summary — ef-043-3, the one new
 * read route this slice adds. `clearedBalance` sums every RECONCILED entry_line's own
 * signed contribution on this account (debit increases, credit decreases — ADR 0001);
 * `difference` is the absolute gap between `statementEndingBalance` (echoed back exactly
 * as given) and `clearedBalance` — zero means the statement balances.
 */
export interface ReconcileSummary {
  clearedBalance: number;
  statementEndingBalance: number;
  difference: number;
  unmatchedStatementLineCount: number;
  unreconciledEntryLineCount: number;
}

/**
 * One row of GET /api/demo/reports/payroll-register — ef-044-3, one employee's own money
 * on one finalized, non-voided pay_run. `ficaEmployer` is the employer's own cost, never
 * withheld from `net`; `gross - (ficaEmployee + federalWh + stateWh) === net` exactly,
 * the same identity `pay_run_line_net_matches_formula` enforces server-side.
 */
export interface PayrollRegisterRow {
  payRunId: Guid;
  employeeId: Guid;
  employeeNumber: string;
  employeeName: string;
  gross: number;
  ficaEmployee: number;
  ficaEmployer: number;
  federalWh: number;
  stateWh: number;
  net: number;
  /** ef-060 — the piece-ticket part of `gross`; 0 when none. */
  piecePay: number;
}

/**
 * Response of GET /api/demo/reports/payroll-register — ef-044-3. One row per employee,
 * either from a single finalized run or summed across every finalized, non-voided run on
 * one entity in a date range — see `demoApi.getPayrollRegister`'s own remarks for the two
 * modes.
 */
export interface PayrollRegisterReport {
  rows: PayrollRegisterRow[];
}

/**
 * Response of POST /api/demo/books/close-year — ef-043-8. `netIncome` is positive when
 * income exceeded expense for the year (credited to 3900 Retained Earnings), negative for
 * a net loss (debited) — informational only, the real posting is the `close_year` entry's
 * own lines (see BooksCloseYearRepository.CloseYearAsync's own remarks).
 */
export interface CloseYearResult {
  entryId: Guid;
  lockedThrough: string; // "YYYY-MM-DD"
  netIncome: number;
}

/**
 * Response of GET /api/demo/books/close-year/status — ef-043-8. `lockedThrough` is null
 * when this entity carries no lock at all (entity-scoped or tenant-wide).
 */
export interface YearLockStatus {
  entityId: Guid;
  lockedThrough: string | null; // "YYYY-MM-DD"
}

/**
 * One line of GET /api/demo/reports/schedule-f — ef-044-2. `label` is the official IRS
 * line description when the server recognises `line`, null otherwise: the column that
 * backs `line` carries no fixed vocabulary (see `SchedFReportContracts.cs`'s own remarks),
 * so an unrecognized line renders with no label rather than a guessed one.
 */
export interface ScheduleFLine {
  line: string;
  label: string | null;
  total: number;
}

/**
 * Response of GET /api/demo/reports/schedule-f — ef-044-2, the Schedule F worksheet:
 * cash-basis income and expense for one entity and one tax year, grouped by the real IRS
 * line each account maps onto (`account.schedule_f_line`, db/migrations/0055).
 * `grossIncome`/`totalExpenses`/`netFarmProfit` are sums of `incomeLines`/`expenseLines`
 * ONLY — `unmappedTotal` is reported separately rather than folded into a figure this
 * worksheet's own name claims is a Schedule F total (see `SchedFReportRepository.cs`'s own
 * remarks). Not a filing — see `ReportsScheduleF.tsx`'s own on-screen disclaimer.
 */
export interface ScheduleFReport {
  entityId: Guid;
  taxYear: number;
  incomeLines: ScheduleFLine[];
  expenseLines: ScheduleFLine[];
  unmappedTotal: number;
  grossIncome: number;
  totalExpenses: number;
  netFarmProfit: number;
}

/**
 * One vendor's line of GET /api/demo/reports/1099-nec — ef-047-5. `settledTotal` +
 * `directTotal` = `total`, by construction (see `Vendor1099ReportRepository.cs`'s own
 * remarks on the two payment paths this sums without double-counting).
 */
export interface Vendor1099Vendor {
  vendorId: Guid;
  name: string;
  settledTotal: number;
  directTotal: number;
  total: number;
  /** ef-064-17 anchor - the vendor's 1099 flag. */
  form1099: Form1099;
  /** ef-064-17 anchor - `ready` | `missing` | `exempt` (same word as `VendorTax.status`). */
  taxStatus: VendorTaxStatus;
  /** ef-064-17 anchor - `***-**-1234` / `**-***1234`, or null. The full number never arrives here. */
  tinMasked: string | null;
}

/**
 * Response of GET /api/demo/reports/1099-nec — ef-047-5, the 1099-NEC worksheet:
 * cash-basis nonemployee compensation for one entity and one tax year, per vendor at or
 * above the $600 IRS reporting threshold. `belowThresholdCount` is how many vendors were
 * paid something this year but stayed under $600 — real activity, just not reportable.
 * `taxIdCaptured` is always `false`: no vendor TIN is stored anywhere in this system (see
 * `Vendor1099ReportRepository.cs`'s own remarks). Not a filing — see `YearEnd.tsx`'s own
 * on-screen disclaimer.
 */
export interface Vendor1099Report {
  entityId: Guid;
  taxYear: number;
  vendors: Vendor1099Vendor[];
  belowThresholdCount: number;
  taxIdCaptured: boolean;
  /** ef-064-17 anchor - listed vendors flagged `nec` with no tax ID on file. */
  missingTinCount: number;
  /** ef-064-17 anchor - groups of two or more listed vendors sharing one tax ID (a warning, never a refusal). Masked. */
  duplicateTins: Vendor1099DuplicateTinGroup[];
}

/** ef-064-17 anchor - two or more worksheet vendors with the same tax ID. */
export interface Vendor1099DuplicateTinGroup {
  tinType: TinType;
  tinLast4: string;
  vendors: { vendorId: Guid; name: string }[];
}

/**
 * One (kind, unit) row of GET /api/demo/reports/production-sales — ef-050-3. `kind` is
 * one of `Harvest`/`Grain`/`Milk`/`Livestock`; `unit` is that kind's own unit of measure
 * (always `hd` for Livestock). Two rows never share a (kind, unit) pair — see
 * `ProductionSalesReportRepository.cs`'s own remarks.
 */
export interface ProductionSalesRow {
  kind: string;
  unit: string;
  qty: number;
  amount: number;
}

/**
 * Response of GET /api/demo/reports/production-sales — ef-050-3, the production & sales
 * worksheet: qty/amount per (kind, unit), for one entity and one caller-chosen window.
 * Grain and Livestock rows are summed over the entity's whole history regardless of
 * `start`/`end` — a confirmed schema gap (`ProductionSalesReportRepository.cs`'s own
 * remarks): neither source carries a caller-settable transaction date.
 */
export interface ProductionSalesReport {
  entityId: Guid;
  start: string;
  end: string;
  rows: ProductionSalesRow[];
}

/**
 * One vendor's line of GET /api/demo/reports/vendor-purchases — ef-052-3.
 * `counterpartyId` is null for the `Unspecified` row (every bill with no counterparty on
 * it) — see `VendorPurchasesReportRepository.cs`'s own remarks.
 */
export interface VendorPurchasesRow {
  counterpartyId: Guid | null;
  name: string;
  total: number;
}

/**
 * Response of GET /api/demo/reports/vendor-purchases — ef-052-3, the vendor purchases
 * summary: bills' face amount, per vendor, for a caller-chosen window. NO `entityId` —
 * unlike `ProductionSalesReport`, this report rolls up every bill on the tenant
 * regardless of entity.
 */
export interface VendorPurchasesReport {
  start: string;
  end: string;
  rows: VendorPurchasesRow[];
}

/**
 * One (herd, status) row of GET /api/demo/reports/livestock-inventory — ef-052-5.
 * `herdName` is the animal's CURRENT herd (`"Unassigned"` when it has none — same null
 * convention `ProductionSalesRow`'s own Vendor1099 sibling uses for "Unspecified").
 * `status` is one of `active`/`sold`/`deceased`/`culled` — a bucket never folds one
 * status into another. `lastTransactionDate` is the bucket's own most recent
 * purchase/sale date, or `null` when none of its animals has ever posted one (a plain
 * status change carries no transaction date at all — see
 * `LivestockInventoryReportRepository.cs`'s own remarks).
 */
export interface LivestockInventoryRow {
  herdName: string;
  status: string;
  count: number;
  lastTransactionDate: string | null;
}

/**
 * Response of GET /api/demo/reports/livestock-inventory — ef-052-5, the CURRENT snapshot
 * (D4 — no `start`/`end`, unlike `ProductionSalesReport` above: this report has no
 * window to choose). `activeTotal` is JSON-only, the sum of every `active`-status row's
 * own `count` — never a second query.
 */
export interface LivestockInventoryReport {
  entityId: Guid;
  rows: LivestockInventoryRow[];
  activeTotal: number;
}

/**
 * One (field, crop, unit) row of GET /api/demo/reports/crop-production — ef-052-4. Two
 * harvests on the same field AND crop sum into one row; two crops on the same field are
 * two separate rows — see `CropProductionReportRepository.cs`'s own remarks.
 */
export interface CropProductionRow {
  fieldName: string;
  crop: string;
  unit: string;
  qty: number;
  amount: number;
}

/**
 * Response of GET /api/demo/reports/crop-production — ef-052-4, the crop production
 * worksheet: qty/amount per (field, crop, unit), for one entity and one caller-chosen
 * window.
 */
export interface CropProductionReport {
  entityId: Guid;
  start: string;
  end: string;
  rows: CropProductionRow[];
}

/**
 * One calendar-month row of GET /api/demo/reports/cash-flow — ef-052-1. `month` is
 * `YYYY-MM`. `net` is `inflow` minus `outflow`, already computed server-side — never
 * re-derived client-side (`format.ts`'s own "nothing here sums" discipline).
 */
export interface CashFlowMonth {
  month: string;
  inflow: number;
  outflow: number;
  net: number;
}

/**
 * Response of GET /api/demo/reports/cash-flow — ef-052-1, the owner's historical cash
 * flow, ACTUAL: inflow/outflow/net per calendar month, for one entity and one
 * caller-chosen window. The forward-looking counterpart is the Home dashboard's own
 * `useCashFlowPlanner` (`GET /api/demo/home/cash-flow`) — a different route, a different
 * shape, never merged.
 */
export interface CashFlowReport {
  entityId: Guid;
  start: string;
  end: string;
  months: CashFlowMonth[];
}

/**
 * One outstanding document on GET /api/demo/reports/aging — ef-052-2. `bucket` is one of
 * `current`/`1-30`/`31-60`/`61-90`/`90+`, computed server-side from `dueOn` and the
 * caller's own `asOf` (`AgingReportRepository.cs`'s own remarks) — never derived on the
 * client.
 */
export interface AgingRow {
  documentId: Guid;
  kind: string;
  counterparty: string;
  outstanding: number;
  bucket: string;
}

/**
 * Response of GET /api/demo/reports/aging — ef-052-2, the AR/AP aging detail worksheet:
 * one row per outstanding document, bucketed by days overdue as of `asOf`, for one entity.
 */
export interface AgingReport {
  entityId: Guid;
  asOf: string;
  rows: AgingRow[];
}

// ---- ef-053-0a: five report types, pre-registered as scaffolding (D1,
// `ef-053-dispatch.md`) — one per report slice, final shape (each slice's builder
// implements against these AS SPECIFIED, never renames a field). ----

/**
 * Response of GET /api/demo/reports/income-statement — ef-053-1. Same shape as the
 * whole-farm `ProfitAndLossReport` above, scoped to ONE entity (`entityId`, REQUIRED on
 * this route — D2).
 */
export interface IncomeStatementReport {
  entityId: Guid;
  basis: "Cash" | "Accrual";
  income: ProfitAndLossLine[];
  expenses: ProfitAndLossLine[];
  incomeTotal: number;
  expenseTotal: number;
  netIncome: number;
}

/**
 * One (bin, crop) balance on GET /api/demo/reports/grain-inventory — ef-053-2. Current
 * on-hand only, no date range (same snapshot convention `livestock-inventory` already
 * established, D3/D4-precedent). A (bin, crop) pair whose movements currently net to
 * zero is absent, never a zero row.
 */
export interface GrainInventoryRow {
  binId: Guid;
  binName: string;
  crop: string;
  onHand: number;
  /** ef-063 anchor — which side of the on-farm / at-elevator split this row counts toward. Optional on this type
   *  so the page tolerates a response written before 0089 (the stubbed `reports-grain-inventory.spec.ts`). */
  locationKind?: GrainLocationKind;
  /** ef-063 anchor — the elevator vendor's name; `null`/absent for a farm bin. */
  vendorName?: string | null;
}

/** ef-063 anchor — one crop's net quantity in one bucket of `GrainInventoryTotals`. */
export interface GrainInventoryCropTotal {
  crop: string;
  qty: number;
}

/** ef-063 anchor — per-crop subtotals (crops are never summed together: their units differ). For every crop
 *  `all = onFarm + atElevator`, and `all` is what the report's total was before 0089. */
export interface GrainInventoryTotals {
  onFarm: GrainInventoryCropTotal[];
  atElevator: GrainInventoryCropTotal[];
  all: GrainInventoryCropTotal[];
}

/** ef-063 anchor — one ACTIVE elevator bin, listed even when empty (`onHand` is `0`, not missing). */
export interface GrainInventoryElevator {
  binId: Guid;
  binName: string;
  vendorId: Guid;
  vendorName: string;
  onHand: number;
}

export interface GrainInventoryReport {
  entityId: Guid;
  rows: GrainInventoryRow[];
  /** ef-063 anchor — optional on this type: absent in a response written before 0089. */
  totals?: GrainInventoryTotals;
  /** ef-063 anchor — optional on this type, see `totals`. */
  elevators?: GrainInventoryElevator[];
}

/**
 * One (month, unit) row on GET /api/demo/reports/milk-production — ef-053-3. `unit` is
 * `"gal"` or `"cwt"`, never summed across units (D4) — a month with both stays two rows.
 */
export interface MilkProductionRow {
  month: string;
  unit: "gal" | "cwt";
  qty: number;
  amount: number;
}

export interface MilkProductionReport {
  entityId: Guid;
  start: string;
  end: string;
  rows: MilkProductionRow[];
}

/**
 * One application row on GET /api/demo/reports/field-applications — ef-053-4. `cost` is
 * null when nothing was spent (or not yet known) — rendered blank, never `$0.00`.
 */
export interface FieldApplicationRow {
  fieldId: Guid;
  fieldName: string;
  crop: string;
  applicationType: "chemical" | "fertilizer" | "seed";
  product: string;
  appliedOn: string;
  rate: number | null;
  rateUnit: string | null;
  cost: number | null;
}

export interface FieldApplicationReport {
  entityId: Guid;
  start: string;
  end: string;
  rows: FieldApplicationRow[];
}

/**
 * One partner's row on GET /api/demo/reports/partner-splits — ef-053-5. No dollar
 * column (D6, `rpt_partperc` half only) — percentage only.
 */
export interface PartnerSplitRow {
  id: Guid;
  partnerName: string;
  percentage: number;
}

export interface PartnerSplitReport {
  entityId: Guid;
  rows: PartnerSplitRow[];
}

// ---- ef-054-0a: five more report types, pre-registered as scaffolding (D1,
// `ef-054-dispatch.md`) — one per report slice, final shape (each slice's builder
// implements against these AS SPECIFIED, never renames a field). ----

/**
 * One partner's row on GET /api/demo/reports/partner-splits-dollar — ef-054-1, the
 * dollar half of the legacy `rptctl_parttran` pair (`partner-splits` above already
 * shipped the percentage half, `rpt_partperc`). `amount` = `percentage / 100 *
 * netIncome` — see `ReportsPartnerSplits.tsx`'s own D5 rationale (ef-054-dispatch.md).
 */
export interface PartnerSplitDollarRow {
  id: Guid;
  partnerName: string;
  percentage: number;
  amount: number;
}

export interface PartnerSplitDollarReport {
  entityId: Guid;
  basis: "Cash" | "Accrual";
  netIncome: number;
  rows: PartnerSplitDollarRow[];
}

/**
 * One `entry_line` row on GET /api/demo/reports/gl-detail — ef-054-2. `side` names the
 * raw `entry_line.side` column; `runningBalance` is cumulative over the ACCOUNT's whole
 * history in `occurredOn`/`createdAt`/`id` order (same "computed chronologically before
 * paging" rule `RegisterEntry.runningBalance` already established), stable across pages
 * of the SAME keyset-cursor fetch.
 */
export interface GlDetailRow {
  id: Guid;
  occurredOn: string;
  side: "Debit" | "Credit";
  amount: number;
  runningBalance: number;
  memo: string | null;
  vendorName: string | null;
  source: string;
  /** ef-064-7 anchor (FROZEN) - THIS line's own `entry_line.quantity` (null when it has none). */
  quantity: number | null;
  /** ef-064-7 anchor (FROZEN) - the unit of `quantity`; null exactly when it is. */
  unit: string | null;
}

/** Cursor-paginated, same shape as `getRegister`'s own page response — see
 *  `GlDetailRow`'s own remarks for why offset pagination is not used here. */
export interface GlDetailPage {
  accountId: Guid;
  accountNumber: string;
  accountName: string;
  entries: GlDetailRow[];
  nextCursor: string | null;
}

/** One account's balance on GET /api/demo/reports/trial-balance — ef-054-3. Exactly one
 *  of `debit`/`credit` is nonzero per row (the account's own net side); an account with
 *  zero activity is absent (empty-state convention, matches every sibling report). */
export interface TrialBalanceRow {
  accountId: Guid;
  number: string;
  name: string;
  kind: "income" | "expense" | "asset" | "liability" | "equity";
  debit: number;
  credit: number;
}

export interface TrialBalanceReport {
  asOf: string;
  rows: TrialBalanceRow[];
  totalDebits: number;
  totalCredits: number;
}

/** One `entry_line` row on GET /api/demo/reports/cash-receipts — ef-054-4, the debit
 *  side of a cash-kind (Asset) account only — narrowed from the legacy `rpt_tran_reg_dtl`
 *  (D7). Cursor-paginated, same reasoning as `GlDetailRow`. */
export interface CashReceiptsRow {
  id: Guid;
  occurredOn: string;
  accountId: Guid;
  accountName: string;
  amount: number;
  memo: string | null;
  vendorName: string | null;
  source: string;
}

export interface CashReceiptsPage {
  start: string;
  end: string;
  entries: CashReceiptsRow[];
  nextCursor: string | null;
}

/** One finalized pay run's own tax columns on GET
 *  /api/demo/reports/payroll-tax-liability — ef-054-5, a deposit-liability summary (D8),
 *  never a 941/943 filing computation. Sums `pay_run_line.fica_employee/
 *  fica_employer/federal_wh/state_wh` — a non-finalized run (all four NULL, 0030's own
 *  CHECK) is excluded, never rendered as zero. */
export interface PayrollTaxLiabilityRow {
  payRunId: Guid;
  payPeriodEnd: string;
  ficaEmployee: number;
  ficaEmployer: number;
  federalWh: number;
  stateWh: number;
}

export interface PayrollTaxLiabilityReport {
  start: string;
  end: string;
  rows: PayrollTaxLiabilityRow[];
  totalFicaEmployee: number;
  totalFicaEmployer: number;
  totalFederalWh: number;
  totalStateWh: number;
}

// ---- ef-055-0a: two more report types, pre-registered as scaffolding (D1,
// `ef-055-dispatch.md`) — one per report slice, final shape (each slice's builder
// implements against these AS SPECIFIED, never renames a field). ----

/** One reconciled `entry_line` row on GET /api/demo/reports/reconciliation — ef-055-1
 *  (D5). `side`/`amount` are the raw, unsigned `entry_line` columns (same shape
 *  `GlDetailRow` already uses); `runningBalance` is signed by the SAME natural-balance
 *  rule `BooksReconcileRepository.SignedAmount` already applies (a debit increases an
 *  asset account, a credit decreases it), cumulative over every reconciled line up to and
 *  including this one, ordered by `reconciledOn` then the entry's own chronological
 *  tie-break — never the whole account's unbounded history the way
 *  `RegisterEntry.runningBalance` is (this report is scoped to reconciled lines only). */
export interface ReconciledLine {
  entryLineId: Guid;
  entryId: Guid;
  occurredOn: string;
  reconciledOn: string;
  side: "Debit" | "Credit";
  amount: number;
  runningBalance: number;
  memo: string | null;
  vendorName: string | null;
}

/** One still-open (unmatched) `bank_statement_line` row, same report — ef-055-1. */
export interface OpenStatementLine {
  id: Guid;
  occurredOn: string;
  amount: number;
  description: string;
}

export interface ReconciliationReport {
  accountId: Guid;
  accountNumber: string;
  accountName: string;
  asOf: string;
  clearedBalance: number;
  reconciledLines: ReconciledLine[];
  openStatementLines: OpenStatementLine[];
}

/** One field's own planted crop-year row on GET /api/demo/reports/crop-planting —
 *  ef-055-2 (D6), reading `field_crop` (0059) directly: (field, crop, acres) for one
 *  entity and year. Two crops on the same field/year (double-cropping) are two separate
 *  rows — `field_crop`'s own UNIQUE (field_id, year, crop) shape. */
export interface CropPlantingRow {
  fieldCropId: Guid;
  fieldName: string;
  crop: string;
  acres: number;
}

export interface CropPlantingReport {
  entityId: Guid;
  year: number;
  rows: CropPlantingRow[];
}

/** `account.kind`'s own wire values on the four WRITE routes below — deliberately
 *  lowercase, unlike `AccountKind` above (`Account.kind`, which serializes the C#
 *  enum PascalCase). These mirror the `account.kind` CHECK constraint's own values
 *  (`AccountContracts.cs`'s `AccountWriteResponse` — see its own remarks). */
export type AccountWriteKind = "income" | "expense" | "asset" | "liability" | "equity";

/**
 * Response shape of the chart of accounts' four WRITE routes — ef-044-4,
 * db/migrations/0057_account_archive.sql (`POST`/`PATCH`/`archive`/`restore`
 * `/api/demo/accounts`, `AccountContracts.cs`'s `AccountWriteResponse`). Deliberately
 * separate from `Account` above (the `GET /accounts` read shape) rather than reused —
 * same reasoning `PlaceResponse`/`JobCodeResponse` sit beside their own persistence
 * types server-side: the write routes' own `kind` is a plain lowercase string, not the
 * PascalCase `AccountKind` enum `Account.kind` carries.
 */
export interface AccountWriteResult {
  id: Guid;
  number: string;
  name: string;
  kind: AccountWriteKind;
  scheduleFLine: string | null;
  /** Null = active. See db/migrations/0057_account_archive.sql. */
  archivedAt: string | null;
}

/** `POST /api/demo/accounts`. Every field required — unlike `CreatePlaceRequest`/
 *  `CreateJobCodeRequest`, an account's kind has no sensible server-side default. */
export interface CreateAccountRequest {
  number: string;
  name: string;
  kind: AccountWriteKind;
}

/** `PATCH /api/demo/accounts/{id}` — RENAME ONLY, the sole field the server route
 *  accepts (`AccountContracts.cs`'s `UpdateAccountRequest` — number and kind are set
 *  once at creation and never move again). */
export interface UpdateAccountRequest {
  name: string;
}

/**
 * Response of `POST /api/demo/setup/accounts/import` — ef-049-1
 * (`AccountImportContracts.cs`'s `AccountImportResponse`). Unlike `ImportStatementResult`
 * above, there is no `rowsDuplicate`: a duplicate `number` rejects the whole file with 409
 * rather than landing as a per-row outcome alongside a partial success. The two counts
 * always satisfy `rowsParsed === rowsImported + rowsSkipped`.
 */
export interface AccountImportResult {
  rowsParsed: number;
  rowsImported: number;
  rowsSkipped: number;
}

/**
 * Body for POST /api/demo/entries — the phone quick-entry submission.
 *
 * `entityId` IS required by the real endpoint (DemoEndpoints.cs rejects
 * Guid.Empty with a 400). There is no GET /api/demo/entities endpoint in
 * scope to discover it from, and the task's constrained quick-entry flow
 * (vendor, category, amount — no entity picker) has nowhere to source it
 * from either. Discovered via GET /api/demo/entities (useReferenceData) rather
 * than configured — the VITE_DEMO_ENTITY_ID env var it used to need is gone.
 * — an explicit env var, not a guess baked into the code. This is flagged
 * as an open question in the return to the dispatching agent: either a
 * GET /api/demo/entities endpoint should exist, or EntityId should become
 * optional/server-resolved to match a UI with no entity picker.
 */
export interface CreateEntryRequest {
  /** A receipt kept by a prior analyze call, to attach to this entry. ef-006. */
  receiptImageId?: string | null;
  entityId: Guid;
  vendorId: Guid;
  accountId: Guid;
  /**
   * The day the money moved — the date ON the receipt, not the day it was photographed.
   * Omitted for a hand-typed entry, which the server books today.
   *
   * Only ever sent when `isPlausibleReceiptDate` accepts it: the server refuses a future
   * or years-old date, and a visitor's scan must not fail at a booth because the model
   * misread a year. See lib/receiptDate.ts.
   */
  occurredOn?: string;
  /**
   * How the entry was created. The API defaults to Manual, which is right for the
   * quick-entry flow and wrong for the Capture screen — that screen's feed renders
   * only `source === "Receipt"`, so a scanned receipt saved without this is invisible
   * on the very screen that captured it, while the UI announces it "just landed in the
   * feed below". The field was missing from this type entirely, so the screen could not
   * have set it even deliberately.
   */
  source?: "Manual" | "Receipt";
  /** Positive magnitude in dollars. Sign for display is derived from the
   *  selected account's kind (Income => credit, everything else => debit) —
   *  see lib/format.ts `signForAccountKind`. */
  amount: number;
  memo?: string | null;
  /** Optional: omitted entirely when the visitor didn't pick one (e.g. GET
   *  /api/demo/payment-methods 404'd and the picker step was hidden — see
   *  hooks/useReferenceData.ts). Backend `CreateEntryRequest.PaymentMethodId`
   *  is nullable, so this matches. */
  paymentMethodId?: Guid | null;
}

export interface CreateEntryResponse {
  /**
   * True only when the receipt was actually attached. Show "receipt kept" on true alone —
   * telling a farmer their documentation was filed when it was not is the failure worth
   * avoiding, because they find out at audit.
   */
  receiptAttached?: boolean;
  id: Guid;
  /** Present only when this vendor already has a confirmed mapping — the
   *  second-scan "it learned" beat. */
  mappedAccountId: Guid | null;
  /** Present only when the payment instrument is new for this vendor — the
   *  third-scan "it's watching your back" beat. Rendered by
   *  components/PaymentAnomalyNotice.tsx on the quick-entry success screen. */
  anomaly: PaymentAnomaly | null;
}

/** How sure the analyzer is. Always shown — a wrong answer presented as certain
 *  is worse at a booth than an uncertain one the visitor can correct. */
export type ReceiptConfidence = "Low" | "Medium" | "High";

/** Result of POST /api/demo/receipts/analyze. */
export interface ReceiptAnalysis {
  /**
   * The kept photograph. ef-006 — the receipt scan's other half: import and document it
   * so it can be categorised by hand later. Null means the analysis worked but the image
   * was not filed; say so rather than implying it was kept.
   */
  receiptImageId?: string | null;
  vendorId: Guid | null;
  accountId: Guid | null;
  /** True when accountId came from a prior human-confirmed mapping rather than
   *  the model's guess — the signal for the zero-touch second scan. */
  accountIsConfirmed: boolean;
  amount: number | null;
  /** ISO date (yyyy-MM-dd) or null. */
  date: string | null;
  /**
   * How sure the analyzer was — or null when it was never asked. ef-011: a PDF is stored
   * and attached but never read, so "not read" is distinct from "read badly". Render the
   * confidence notice only when this is set; `notAnalyzedReason` explains the null.
   */
  confidence: ReceiptConfidence | null;
  /**
   * Why no reading was attempted, in words to show the user. Null whenever the analyzer
   * actually ran. ef-011 / Q-PDF-ATTACH.
   */
  notAnalyzedReason?: string | null;
}

/** A legal entity belonging to the tenant. The demo seeds one per tenant;
 *  real customers run 2-6 (roadmap FR-2). */
export interface BusinessEntity {
  id: Guid;
  name: string;
  state: string | null;

  /**
   * "weekly" or "biweekly" — ef-015, `entity.pay_schedule`. Was on the wire
   * (`EntityResponse.PaySchedule`) since ef-015 landed but had no client-side reader until
   * ef-034: `TimeEndpoints.PeriodFor` derives every pay period from this, and PayRuns.tsx
   * is the first screen to say so out loud rather than silently assuming biweekly.
   */
  paySchedule: string;

  /**
   * This entity's own anchor payday, or null — a null here defers to
   * `PayrollCalendar.FirstPaydayOf`, which is what every entity computed before this
   * column existed. `YYYY-MM-DD`.
   */
  firstPayday: string | null;

  /** ef-047-2 — null = active. See db/migrations/0066_entity_archive_and_default.sql. */
  archivedAt: string | null;

  /** ef-047-2 — at most one true row per tenant (`entity_one_default_per_tenant`). */
  isDefault: boolean;
}

/**
 * One state paid-leave program this build can compute — GET /api/payroll/paid-leave/programs.
 *
 * ⚠️ CARRIES NO RATES, on purpose. A screen asking "which return does this business file"
 * does not need them, and the wage base is the one figure the engine keeps exactly one copy
 * of. Anything needing the numbers calls the calc.
 *
 * The list is the SERVER's answer, never a constant in the client. A hardcoded list of
 * covered states is how the year-end returns row came to print "MN Paid Leave" on every
 * business in every state — right only because every demo entity is Minnesota.
 */
export interface StatePaidLeaveProgram {
  /** Two-letter state code, upper case. */
  state: string;
  /** What the program is called where it is filed — "Minnesota Paid Leave". */
  name: string;
}

/**
 * The state paid-leave programs configured for a year.
 *
 * An EMPTY `programs` is a real answer — this build has no published rates for that year —
 * and must render as "we do not have this", never as an error and never by falling back to
 * whatever was shown last. `year` is echoed because the request may omit it.
 */
export interface StatePaidLeavePrograms {
  year: number;
  programs: StatePaidLeaveProgram[];
}

/**
 * Wire types for GET /api/demo/reports/profit-loss —
 * src/EasyFarm.Api/Demo/DemoContracts.cs (ProfitAndLossLineResponse / ProfitAndLossResponse).
 *
 * Every figure here is a real sum of the calling tenant's own `entry` rows, grouped by
 * `account` — never a fixture. An account nobody has posted to does not appear as a zero
 * line; it is simply absent (see the API's remarks on the underlying query).
 */
export interface ProfitAndLossLine {
  accountId: Guid;
  number: string;
  name: string;
  amount: number;
}

/**
 * `basis` is always `"Cash"` today. There is deliberately no accrual figure anywhere in
 * this shape — an accrual P&L needs receivables, payables and accrual timing this schema
 * does not carry (no invoice/bill entity, no accrual flag on `entry`), and this client
 * must not invent one. See pages/Reports.tsx for how the Accrual toggle is handled
 * honestly without a second set of numbers from this endpoint.
 */
export interface ProfitAndLossReport {
  /** Echoes what the server actually computed, never what was asked for. */
  basis: "Cash" | "Accrual";
  income: ProfitAndLossLine[];
  expenses: ProfitAndLossLine[];
  /** Sum of `income` — not re-derived client-side. */
  incomeTotal: number;
  /** Sum of `expenses` — not re-derived client-side. */
  expenseTotal: number;
  /** `incomeTotal - expenseTotal`, as computed by the server. */
  netIncome: number;
}

/** One account's balance on the balance sheet — src/EasyFarm.Api/Demo/DemoContracts.cs. */
export interface BalanceSheetLine {
  accountId: string;
  number: string;
  name: string;
  /**
   * Positive when the account sits on its natural side. Signed by the C# domain
   * (`PostingRules.SignedForBalance`), never re-derived here — a credit to an asset
   * reduces it, and that rule has one home.
   */
  balance: number;
}

/**
 * GET /api/demo/reports/balance-sheet — the Reports screen's Net worth card. ef-002 / ADR 0005.
 *
 * `hasOpeningBalances` is the field the screen branches on, and it is not the same question
 * as "is net worth zero". A tenant that has never posted an asset, liability or equity
 * balance has NO balance sheet; rendering $0.00 for it would be a fabricated figure wearing
 * the authority of a real one. The Booth Demo tenant is that case on every deployment.
 */
export interface BalanceSheetReport {
  assets: BalanceSheetLine[];
  liabilities: BalanceSheetLine[];
  equity: BalanceSheetLine[];
  assetTotal: number;
  liabilityTotal: number;
  equityTotal: number;
  /** Income less expenses — the same figure the cash-basis P&L renders. */
  netIncome: number;
  /** Assets less liabilities. */
  netWorth: number;
  /**
   * The balance of the tenant's default cash account (`account.is_default_cash`) — what
   * the Dashboard's Cash on hand card reads. NOT `assetTotal`: a farm's land and machinery
   * are assets it cannot spend, and showing them as cash would be the most confidently
   * wrong number on the screen.
   */
  cashOnHand: number;
  /**
   * Money owed TO the farm — open invoices, face value less settled. ef-003.
   *
   * NOT included in `assetTotal` or `netWorth`: a cash-basis balance sheet has no
   * receivables, and folding it in with no offsetting credit would break the identity
   * `balances` asserts. The Reports screen adds it under the Accrual toggle only.
   */
  receivable: number;
  /** Money the farm owes — open bills, face value less settled. */
  payable: number;
  /**
   * The open-books identity `assets - liabilities = equity + netIncome`, checked server-side
   * in exact decimal. False means these figures are not to be trusted.
   */
  balances: boolean;
  hasOpeningBalances: boolean;
  /**
   * Receipts with no account — count and total, from the LEDGER rather than from whatever
   * slice of the feed is on screen. Not part of any other figure here: an uncategorised
   * entry posts no ledger lines at all, which is exactly why it has to be reported
   * separately. See Dashboard.tsx's insight-strip note for the measurement that moved this
   * off the client.
   */
  uncategorizedReceiptCount: number;
  uncategorizedReceiptTotal: number;
}

/**
 * Wire types for POST /api/payroll/paycheck — src/EasyFarm.Api/Payroll/PaycheckContracts.cs.
 *
 * Verified against the running API (2026-09-01): a request with
 * `{ grossWages: 1886.67, payPeriodsPerYear: 26, taxYear: 2026, filingStatus: "Single" }`
 * returns `socialSecurityEmployee: 116.97` and `medicareEmployee: 27.36` — exactly
 * 6.2% and 1.45% of gross — confirming this is the real, tested engine
 * (EasyFarm.PayrollDeductions), not a stub.
 *
 * UNLIKE every other endpoint in this file, this one is NOT tenant-scoped — it
 * is a pure calculator with no `X-Demo-Tenant` header and no RLS. See
 * `calculatePaycheck` in client.ts, which deliberately does not go through the
 * tenant-requiring `request()` helper the rest of this client uses.
 */
export type FilingStatus = "Single" | "MarriedFilingJointly" | "HeadOfHousehold";

export interface PaycheckRequest {
  /** Gross wages for this pay period. */
  grossWages: number;
  /** 52 weekly, 26 biweekly, 24 semimonthly, 12 monthly. */
  payPeriodsPerYear: number;
  /** Unconfigured years are REJECTED by the API (400), never guessed at with a
   *  stale wage base — see PaycheckValidationError in client.ts. */
  taxYear: number;
  filingStatus: FilingStatus;
  /** YTD wages already subject to Social Security, for the wage-base cap. Zero
   *  for a from-scratch demo run. */
  ytdSsWages?: number;
  /** YTD Medicare wages, for the Additional-Medicare threshold. Zero for a
   *  from-scratch demo run. */
  ytdMedicareWages?: number;
  /** True when this employee's wages are statutorily exempt from Social Security
   *  and Medicare (FICA) — currently H-2A temporary agricultural workers. Does NOT
   *  affect federal income-tax withholding. Omitted/false for every employee this
   *  does not apply to — see PaycheckContracts.cs `FicaExempt` for the IRS citation. */
  ficaExempt?: boolean;
}

/** A computed paystub. Every field is real output from the tested engine —
 *  nothing here is summed or otherwise recomputed on the client (see
 *  lib/format.ts and hooks/usePayrollRun.ts). */
export interface PaycheckResponse {
  gross: number;
  pretaxTotal: number;
  socialSecurityEmployee: number;
  medicareEmployee: number;
  additionalMedicareEmployee: number;
  federalWithholding: number;
  stateWithholding: number;
  otherPostTax: number;
  totalEmployeeTaxes: number;
  netPay: number;
  /** Employer match — a farm cost, not withheld from the check. */
  socialSecurityEmployer: number;
  medicareEmployer: number;
  /** Echoes the request's `ficaExempt` — lets the UI tell "$0.00 because exempt"
   *  apart from "$0.00 because zero wages" without re-deriving it. */
  ficaExempt: boolean;
}

/**
 * Wire types for POST /api/payroll/run — src/EasyFarm.Api/Payroll/PayrollRunContracts.cs.
 *
 * One call computes the whole roster instead of one POST /api/payroll/paycheck per
 * employee (the shape that fired 12 requests on every Payroll screen load). Every
 * field except `employeeId` mirrors PaycheckRequest exactly, and the response mirrors
 * PaycheckResponse per employee plus server-computed run totals — nothing here is
 * summed or otherwise recomputed on the client, same rule as the single-paycheck path.
 */
export interface PayrollRunEmployeeRequest extends PaycheckRequest {
  /** Opaque id correlating this line to its result row. Not validated against any
   *  roster — the engine has no concept of a roster, only what it's given. */
  employeeId: string;
}

export interface PayrollRunRequest {
  employees: PayrollRunEmployeeRequest[];
}

/** One roster line's result. Exactly one of `paycheck` / `errors` is populated,
 *  selected by `success` — a bad employee (negative gross, bad pay-period count,
 *  unconfigured tax year) reports its own error here without dropping or corrupting
 *  any other row in the same run. */
export interface PayrollRunEmployeeResult {
  employeeId: string;
  success: boolean;
  paycheck: PaycheckResponse | null;
  /** Same field->messages shape as ValidationProblem.errors below, so the client
   *  shows the exact server-provided reason per row, not a generic message. */
  errors: Record<string, string[]> | null;
}

/** Run-wide totals, computed server-side from the employee rows the engine actually
 *  produced. Employees with `success: false` contribute nothing here. */
export interface PayrollRunTotalsResponse {
  gross: number;
  /** SS + Medicare + Additional Medicare, employee side. */
  ficaMedicare: number;
  /** Federal + state withholding. */
  withholding: number;
  netPay: number;
}

export interface PayrollRunResponse {
  employees: PayrollRunEmployeeResult[];
  totals: PayrollRunTotalsResponse;
}

/** ASP.NET's Results.ValidationProblem() shape — RFC 7807 problem+json with a
 *  field->messages map. The paycheck endpoint returns this for negative gross,
 *  a non-positive pay-period count, or an unconfigured tax year. */
export interface ValidationProblem {
  title?: string;
  status?: number;
  errors: Record<string, string[]>;
}

/**
 * Wire types for POST /api/payroll/paid-leave —
 * src/EasyFarm.Api/Payroll/PaidLeaveContracts.cs.
 *
 * The HTTP surface over EasyFarm.PaidLeave (PaidLeaveCalculator + WageDetailAggregator
 * + DelimitedWageDetailWriter). Every money figure below is the ENGINE's, including the
 * rates in `program` — the frontend keeps no copy of the MN rate or wage base, so a
 * screen physically cannot render a premium the engine did not compute.
 *
 * The `*Count` fields are not decoration: they are how the response reports what it
 * could NOT do (SSNs it was never given, hours it does not know) so the screen can say
 * so instead of rendering a confident-looking blank.
 */
export interface PaidLeaveEmployeeRequest {
  employeeId: string;
  firstName?: string | null;
  lastName?: string | null;
  /** MN's wage-detail file requires it; null where the roster genuinely has none. */
  ssn?: string | null;
  /** ISO date. Selects the reporting quarter and is compared to the effective date. */
  checkDate: string;
  grossWages: number;
  /** Wages subject to the premium, per the company's covered basis. */
  coveredWages: number;
  /** Covered wages YTD BEFORE this check — drives the annual wage-base cap. */
  ytdCoveredWages: number;
  exempt: boolean;
  /** Null means "not known", NOT zero hours — see hoursPopulatedCount. */
  hours?: number | null;
}

/**
 * Employer-supplied program parameters. Every field is independently optional: an
 * omitted field keeps the published figure, so an employer entitled to MN's
 * small-employer reduced rate sends `rateEmployer` alone.
 *
 * Rates are FRACTIONS on the wire, matching the engine — 0.44% is `0.0044`. The screen
 * collects percentages because that is what a rate is called on a state's website; the
 * conversion is a unit transform on the way out, the mirror image of the display-side
 * one, and is the only arithmetic this app performs on a rate.
 */
export interface PaidLeaveProgramOverrideRequest {
  rateEmployee?: number | null;
  rateEmployer?: number | null;
  wageBase?: number | null;
}

export interface PaidLeaveRequest {
  state: string;
  taxYear: number;
  /** Omit to have the server derive the reporting quarter from the check dates. */
  year?: number | null;
  quarter?: number | null;
  employerAccountId?: string | null;
  /** Omit for the published defaults — that path is unchanged by this field existing. */
  program?: PaidLeaveProgramOverrideRequest | null;
  employees: PaidLeaveEmployeeRequest[];
}

/** The catalog's published, sourced figures — always returned, whether or not they were
 *  overridden. This is the provenance: what the effective figures would have been. */
export interface PublishedProgramResponse {
  rateEmployee: number;
  rateEmployer: number;
  wageBase: number;
}

/** The program parameters the calculation actually used — the engine's
 *  StateProgramCatalog row, or the employer's own where they supplied one. Never a
 *  constant in this app either way. */
export interface PaidLeaveProgramResponse {
  state: string;
  /** Fraction, e.g. 0.0044 for 0.44%. THE EFFECTIVE VALUE — what every premium in this
   *  response was actually computed from. */
  rateEmployee: number;
  rateEmployer: number;
  wageBase: number;
  effectiveOn: string;
  /** True while the catalog's PUBLISHED wage base is the engine's own approximation
   *  rather than a confirmed published figure. The screen must say so when this is true.
   *  Describes `published`, and is unaffected by an override. */
  wageBaseIsProvisional: boolean;
  /** What the catalog publishes, so an overridden figure is never shown alone. */
  published: PublishedProgramResponse;
  /** Per-field provenance, decided by the SERVER by comparing values. The screen renders
   *  these flags; it never compares the numbers itself and never decides what counts as
   *  an override. */
  rateEmployeeOverridden: boolean;
  rateEmployerOverridden: boolean;
  wageBaseOverridden: boolean;
  isOverridden: boolean;
}

export interface PaidLeavePremiumResponse {
  employeePremium: number;
  employerPremium: number;
  /** Wages actually assessed after the annual cap. */
  coveredThisCheck: number;
  /** False when the engine declined to assess (exempt, pre-effective, no covered
   *  wages, capped out). A real answer, not a failure — see `success`. */
  applied: boolean;
}

export interface PaidLeaveEmployeeResult {
  employeeId: string;
  success: boolean;
  premium: PaidLeavePremiumResponse | null;
  errors: Record<string, string[]> | null;
}

export interface PaidLeaveTotalsResponse {
  employeePremium: number;
  employerPremium: number;
  coveredWages: number;
  totalRemittance: number;
  appliedCount: number;
  /** Computed but not assessed — distinct from failedCount. */
  notAppliedCount: number;
  failedCount: number;
}

export interface WageDetailRecordResponse {
  /** The aggregation key: a real SSN, or the employeeId where none was supplied. */
  ssn: string;
  ssnSupplied: boolean;
  firstName: string | null;
  lastName: string | null;
  totalWages: number;
  pflCoveredWages: number;
  pflEmployeePremium: number;
  pflEmployerPremium: number;
  /**
   * Summed hours, or NULL where no line in the group stated any — see `hoursPopulatedCount`.
   *
   * IT USED TO BE `number`, and the null was flattened to 0 at the API boundary. The screen
   * inferred "unknown" back out of `hours > 0`, which cannot tell an unknown from a genuine
   * zero, and the downloadable file wrote `0.00` — a claim that the person worked no hours,
   * in the artifact that would be filed.
   */
  hours: number | null;
}

export interface WageDetailResponse {
  year: number;
  quarter: number;
  quarterDerived: boolean;
  employerAccountId: string | null;
  records: WageDetailRecordResponse[];
  totalCoveredWages: number;
  totalRemittance: number;
  lineCount: number;
  linesOutsideQuarter: number;
  /** Below records.length means the generated file is NOT submittable as-is. */
  ssnPopulatedCount: number;
  /** Below lineCount means the Hours column is understated, not zero-hours. */
  hoursPopulatedCount: number;
  /** DelimitedWageDetailWriter's output, verbatim. */
  delimitedFile: string;
  delimitedFileName: string;
  /** The writer's own caveat: column ORDER must be aligned to MN's template before a
   *  real submission. The data is firm. */
  columnOrderIsProvisional: boolean;
}

export interface PaidLeaveResponse {
  program: PaidLeaveProgramResponse;
  employees: PaidLeaveEmployeeResult[];
  totals: PaidLeaveTotalsResponse;
  wageDetail: WageDetailResponse;
}

/**
 * GET /api/demo/usage-counts (feature/auth) — how many entries exist per
 * vendor/account, keyed by id. Deliberately the ENTIRE shape: no amounts, memos,
 * or timestamps travel over this endpoint, because it is public/unauthenticated
 * (see useUsageCounts.ts) and `Entry`'s full shape is not.
 */
export interface UsageCounts {
  vendors: Record<Guid, number>;
  accounts: Record<Guid, number>;
}

/**
 * The authenticated user, as returned by POST /api/auth/login and GET /api/auth/me.
 * Deliberately minimal — no tenant id, no session token (that only ever travels as
 * an HttpOnly cookie the JS layer cannot read), nothing beyond what the UI needs to
 * render "logged in as ___".
 */
export interface SessionUser {
  email: string;
  /**
   * The caller's OWN role. Navigation only — see SessionUserResponse.Role on the API for
   * the full note. Every route decides for itself from the session row; nothing here is
   * an access control, and forging it in devtools grants nothing. It exists so an
   * employee is sent to the one screen they may use instead of the owner dashboard,
   * where four fetches would 403 in a row and read as "the app is down".
   */
  role: UserRole;
}

export type UserRole = "owner" | "employee";

/**
 * One person on the payroll roster — GET /api/demo/employees.
 *
 * THIS USED TO BE A FIXTURE. Until `db/migrations/0003_employee.sql` there was no
 * employee table in the schema at all, and the twelve-person roster was a TypeScript
 * array in `pages/payroll-fixtures.ts`. The Payroll screen therefore computed genuinely
 * real withholding — FICA, federal, net pay, all through the tested
 * EasyFarm.PayrollDeductions engine — for twelve people who existed nowhere but the
 * front end, on the one screen whose entire claim is that its numbers are real. Same
 * people and the same figures now arrive from the database, tenant-scoped by RLS like
 * every other row this client reads.
 *
 * Carries no `tenantId` — RLS scopes the query, so the API never echoes it back (same as
 * `Vendor` / `Account` / `Entry` above).
 *
 * `PAY_PERIODS_PER_YEAR`, `FilingStatus` and the pay-period dates are still declared
 * fixtures and still live in `pages/payroll-fixtures.ts`: a real run reads each person's
 * own W-4 elections and pay schedule, which this slice's employee record does not carry.
 * Flagged rather than hidden — see the disclosure rendered on the Payroll screen itself.
 */
export interface PayrollEmployee {
  id: Guid;
  /** Which business this person works for — decides whether their wages go on the
   *  farm's annual Form 943 or a non-farm business's quarterly Form 941. */
  entityId: Guid;
  /** The employee number. Also the order the API returns the roster in — a payroll run
   *  is read top to bottom against last period's, so it must not reshuffle. */
  number: string;
  /** The roster's display name ("Miguel A."), not a first/last split. MN's quarterly
   *  wage detail wants the split; this roster does not have it, and the Paid Leave
   *  screen says so rather than inventing a surname. */
  name: string;
  classification: string;
  /**
   * True for H-2A workers. Forwarded to the payroll calculator as `ficaExempt`
   * (usePayrollRun.ts) — but deliberately NOT forwarded to the paid-leave calculator's
   * exemption flag (usePaidLeave.ts). The two notes below are the reason, and both are
   * load-bearing rather than decoration.
   *
   * CLOSED GAP: the engine now HAS a FICA-exemption input
   * (`EasyFarm.PayrollDeductions.FicaMedicareInput.FicaExempt`, plumbed through
   * `PaycheckRequest.ficaExempt`) for the settled federal rule that H-2A wages are
   * exempt from Social Security and Medicare — see that field's doc comment for the
   * IRS citation. `ficaExemptByLaw` IS now forwarded as `ficaExempt` on every
   * POST /api/payroll/run line (usePayrollRun.ts) — the badged employees' SS/Medicare
   * figures are the real exempted calculation (both employee AND employer side),
   * not the engine's ordinary one. Federal withholding is unaffected, by design —
   * see the disclosure rendered in Payroll.tsx for what that means for net pay.
   *
   * REMAINING KNOWN GAP, on the MN Paid Leave side (see pages/PaidLeave.tsx):
   * `ficaExemptByLaw` is deliberately NOT forwarded as the paid-leave exemption
   * flag. FICA exemption and paid-leave exemption are separate determinations
   * under separate law, and whether MN Paid Leave exempts H-2A agricultural
   * workers is a legal question this repo has not answered — unlike the FICA
   * question above, which federal law already settles. The engine HAS a
   * per-employee exemption input; wiring the FICA flag into it would be inventing
   * an answer, so every line is sent `exempt: false` and the screen says which
   * determination has not been made. Disclosed, not quietly assumed either way.
   */
  ficaExemptByLaw: boolean;
  /** Gross wages for this one pay period, in dollars. */
  grossWages: number;
  /**
   * Hours on this check, where the roster states them — the `classification` strings
   * carry them for the hourly employees and for nobody else.
   *
   * `null` means NOT KNOWN, and is deliberately not collapsed to 0 anywhere between the
   * `employee` table and this field: MN's quarterly wage-detail file has an Hours column,
   * and a salaried employee reported as having worked zero hours is a wrong filing, not a
   * blank one. The API counts how many lines arrived with a real figure
   * (`hoursPopulatedCount`) so the Paid Leave screen can say the column is understated
   * instead of presenting it as complete.
   */
  hoursThisPeriod: number | null;

  /**
   * "hourly" or "salary" — ef-034, `employee.pay_basis`'s own CHECK constraint
   * (0030_pay_run_finalize.sql). Decides how a pay run's Finalize step derives this
   * person's gross: hourly multiplies a run's approved hours by `hourlyRate`; salary uses
   * `grossWages` verbatim.
   */
  payBasis: string;

  /** Dollars per hour for an hourly employee, or null when NOT SET — finalizing a run with
   *  an hourly employee missing a rate is refused rather than paying them $0.00. */
  hourlyRate: number | null;

  /**
   * Form W-4 Step 1(c) — "single", "married_joint" or "head_of_household". ef-038-4,
   * `db/migrations/0036_employee_w4.sql`.
   *
   * NEVER null on the wire: an employee with no election on file reads as "single", the
   * same zeroed default the finalize engine (ef-027) already assumes for everyone — see
   * `EmployeeResponse.From`. This field is NOT wired into finalize or any payroll
   * calculation on this screen; ef-039 is where that would happen.
   */
  filingStatus: string;
  /** Form W-4 Step 2(c) checkbox. */
  multipleJobs: boolean;
  /** Form W-4 Step 3 dependents credit, in dollars. */
  dependentsAmount: number;
  /** Form W-4 Step 4(a) other income, in dollars. */
  otherIncome: number;
  /** Form W-4 Step 4(b) deductions, in dollars. */
  deductions: number;
  /** Form W-4 Step 4(c) extra withholding per period, in dollars. */
  extraWithholding: number;
  /** The year this election was made, or null when no election is on file — unlike
   *  `filingStatus`, this one is not defaulted. */
  w4Year: number | null;
  /** ef-058 anchor. `false` once an owner deactivates this person — they leave the
   *  schedule/clock pick-lists but stay on every report and history. */
  active: boolean;
  /** ef-058 anchor. Free-text mailing address, or null. PII — never log it. */
  address: string | null;
  /** ef-058 anchor. `"YYYY-MM-DD"`, or null when never entered. */
  startDate: string | null;
}

/** `PATCH /api/demo/employees/{id}/w4` — ef-038-4. A true partial update: every field is
 *  optional, and a field left off the request keeps its current value server-side. There
 *  is no way to clear a field back to its zero/NULL default through this request shape. */
export interface UpdateEmployeeW4Request {
  /** Must be one of "single" / "married_joint" / "head_of_household" when supplied. */
  filingStatus?: string;
  multipleJobs?: boolean;
  /** `>= 0` when supplied. */
  dependentsAmount?: number;
  /** `>= 0` when supplied. */
  otherIncome?: number;
  /** `>= 0` when supplied. */
  deductions?: number;
  /** `>= 0` when supplied. */
  extraWithholding?: number;
  w4Year?: number;
}

// ---- ef-058 anchors: employee create / edit / rate history ----------------------------

/** `POST /api/demo/employees` — ef-058-2. No SSN field, by decision D3. */
export interface CreateEmployeeRequest {
  entityId: Guid;
  /** 1-120 characters after trimming. */
  name: string;
  /** At most 300 characters. */
  address?: string | null;
  /** `"YYYY-MM-DD"`; also the effective date of the first rate-history row. */
  startDate?: string | null;
  payBasis: "hourly" | "salary";
  /** Hourly: $/hour. Salary: $/pay period. null = not set (legal for hourly only). */
  rate: number | null;
  /** The H-2A FICA determination. */
  ficaExemptByLaw: boolean;
}

/** `PATCH /api/demo/employees/{id}` — ef-058-2. A true partial update; no rate field. */
export interface UpdateEmployeeRequest {
  name?: string;
  /** Empty string clears the address. */
  address?: string;
  startDate?: string;
  ficaExemptByLaw?: boolean;
  /** `false` deactivates (never deletes); `true` reactivates. */
  active?: boolean;
}

/** `POST /api/demo/employees/{id}/rates` — ef-058-2, a dated rate change. */
export interface ChangeEmployeeRateRequest {
  payBasis: "hourly" | "salary";
  rate: number | null;
  /** `"YYYY-MM-DD"`, 2000-01-01 through today + 366 days. */
  effectiveFrom: string;
}

/** One row of `GET /api/demo/employees/{id}/rates` (newest `effectiveFrom` first). */
export interface EmployeeRateRow {
  id: Guid;
  employeeId: Guid;
  payBasis: string;
  rate: number | null;
  effectiveFrom: string; // "YYYY-MM-DD"
  /** null on the backfilled/origin row. */
  changedBy: Guid | null;
  changedAt: string; // ISO 8601
}

export interface EmployeeRateWarning {
  /** `"finalized_run_covers_date"` — the only code today. */
  code: string;
  message: string;
  payRunId?: Guid | null;
  periodStart?: string | null;
  periodEnd?: string | null;
}

/** Response of `POST /api/demo/employees/{id}/rates` (201). */
export interface ChangeEmployeeRateResponse {
  rate: EmployeeRateRow;
  employee: PayrollEmployee;
  warnings: EmployeeRateWarning[];
}

/** Request body for POST /api/payroll/year-end. Reuses PayrollRunEmployeeRequest so the
 *  roster shape the Payroll screen already builds is the roster this sends. */
export interface YearEndRequest {
  /** Periods to iterate. Bounded 1-366 server-side. */
  payPeriodsToProject: number;
  employees: PayrollRunEmployeeRequest[];
}

/**
 * POST /api/payroll/year-end — IRS Form 943 and the roster's W-2 boxes.
 *
 * ⚠️ `basis` is `"Projected"`, and that is not decoration. Nothing in this system
 * stores a payroll run (there is no paycheck history table), so these are not
 * year-to-date figures and not filed figures: they are what the annual forms look
 * like if the year runs at the roster's current rate. Any screen showing them must
 * say so. See the remarks on YearEndRequest in the API for the full reasoning.
 */
export interface Form943Lines {
  socialSecurityWages: number;
  socialSecurityTax: number;
  medicareWages: number;
  medicareTax: number;
  additionalMedicareWages: number;
  additionalMedicareTax: number;
  federalIncomeTaxWithheld: number;
  totalTaxes: number;
  /** 0.124 — the combined employer+employee SS rate, from the engine. */
  socialSecurityCombinedRate: number;
  /** 0.029 — likewise, Medicare. */
  medicareCombinedRate: number;
  /** How many roster lines were H-2A and therefore excluded from the wage lines. */
  ficaExemptEmployeeCount: number;
}

/** One employee's W-2 boxes. Boxes 3–6 are zero — not merely small — when `ficaExempt`. */
export interface W2Lines {
  employeeId: Guid;
  box1FederalWages: number;
  box2FederalTax: number;
  box3SocialSecurityWages: number;
  box4SocialSecurityTax: number;
  box5MedicareWages: number;
  box6MedicareTax: number;
  ficaExempt: boolean;
}

export interface YearEndEmployeeError {
  employeeId: Guid;
  errors: Record<string, string[]>;
}

export interface YearEndForms {
  basis: string;
  taxYear: number;
  payPeriodsProjected: number;
  form943: Form943Lines;
  w2s: W2Lines[];
  /** Roster lines the engine could not project, with the reason. */
  excluded: YearEndEmployeeError[];
}

/** Request body for POST /api/payroll/quarterly (IRS Form 941). */
export interface QuarterlyRequest {
  /** 1-4. Q1 Jan-Mar, Q2 Apr-Jun, Q3 Jul-Sep, Q4 Oct-Dec. */
  quarter: number;
  payPeriodsInQuarter: number;
  employees: PayrollRunEmployeeRequest[];
}

/** Form 941 Part 1 lines. Same statutory rates as the 943; different cadence, different
 *  line numbering, and a Line 2 the 943 has no equivalent for. */
export interface Form941Lines {
  employeeCount: number;
  wagesTipsOtherCompensation: number;
  federalIncomeTaxWithheld: number;
  socialSecurityWages: number;
  socialSecurityTax: number;
  medicareWages: number;
  medicareTax: number;
  additionalMedicareWages: number;
  additionalMedicareTax: number;
  totalSocialSecurityAndMedicareTaxes: number;
  totalTaxesBeforeAdjustments: number;
  socialSecurityCombinedRate: number;
  medicareCombinedRate: number;
  additionalMedicareRate: number;
  /** Expected to be 0. Non-zero means the FARM's roster was sent to the 941. */
  unexpectedFicaExemptCount: number;
}

export interface QuarterlyForm {
  basis: string;
  taxYear: number;
  quarter: number;
  payPeriodsProjected: number;
  form941: Form941Lines;
  excluded: YearEndEmployeeError[];
}

/** One quarter's filing position — see FilingStatusResponse in the API. */
export interface QuarterStatus {
  quarter: number;
  startDate: string;
  endDate: string;
  dueDate: string;
  /** Differs between quarters, and that is correct: 26 biweekly paydays do not divide
   *  into four, so two quarters carry seven and two carry six. */
  payPeriods: number;
  /** True only when a real filing row exists — never inferred from the date. */
  filed: boolean;
  filedOn: string | null;
  totalTaxes: number | null;
}

export interface FilingCalendar {
  entityId: Guid;
  taxYear: number;
  /** The SERVER's date. A laptop with a wrong clock must not be able to hide a late return. */
  today: string;
  quarters: QuarterStatus[];
}

/* ---------------------------------------------------------------------------
 * Employee time entry — /api/demo/time/*
 *
 * NOTE WHAT IS ABSENT FROM EVERY REQUEST TYPE BELOW: an employee id. Not on a punch,
 * not on a manual entry, not on the listing. The server resolves the subject from the
 * session row (TimeEndpoints.TryOpenEmployeeSessionAsync), and a field here that named
 * an employee would let one worker file time against a colleague while looking entirely
 * reasonable in review. If a future change seems to need one, the need is the bug.
 * ------------------------------------------------------------------------- */

/** How a business records time — `entity.time_entry_mode`. */
export type TimeEntryMode = "punch" | "manual" | "both";

/** Which way one row was recorded. Both produce the same payable artefact. */
export type TimeEntrySource = "punch" | "manual";

/**
 * `open` is a punch still running. `submitted` is a claim awaiting the farmer —
 * everything lands there, whichever way it was recorded. `approved` is the only status
 * that reaches payroll.
 */
export type TimeEntryStatus = "open" | "submitted" | "approved" | "rejected";

/**
 * `place.kind`'s own CHECK constraint (db/migrations/0026_place.sql) — kept as a union
 * here so a typo in a `<select>` value is a compile error rather than a 400 discovered at
 * submit time. Mirrors `PlaceEndpoints.AllowedKinds` on the API; the API's own constraint
 * remains the actual enforcement, this is just the same list read twice.
 */
export type PlaceKind = "field" | "yard" | "shop" | "house" | "other";

/**
 * ef-021 — one of the farm's own named locations, GET/POST/PATCH/DELETE
 * `/api/demo/places`. Owner-only: naming the farm's own ground is a business record, not
 * something an employee login reaches directly — they read it only indirectly, through a
 * resolved `startPlace`/`endPlace` on their own punches (see `PunchLocation` below).
 */
export interface Place {
  id: Guid;
  name: string;
  kind: PlaceKind;
  lat: number;
  lon: number;
  /** How far from the centre point a punch still counts as "at" this place. */
  radiusYards: number;
  /** Null = active. Set by DELETE; cleared by `POST /places/{id}/unarchive` (ef-038-3),
   *  the only other writer of this column. */
  archivedAt: string | null;
  createdAt: string;
}

/** `POST /api/demo/places`. `kind` and `radiusYards` are optional — the SERVER defaults
 *  them ('field', 440 yd / ¼ mile), so there is exactly one place either default lives. */
export interface CreatePlaceRequest {
  name: string;
  kind?: PlaceKind;
  lat: number;
  lon: number;
  radiusYards?: number;
}

/** `PATCH /api/demo/places/{id}`. Every field optional — whatever is omitted keeps its
 *  current value server-side. A partial update, never a full replace. */
export interface UpdatePlaceRequest {
  name?: string;
  kind?: PlaceKind;
  lat?: number;
  lon?: number;
  radiusYards?: number;
}

/**
 * The place a punch resolved to — a small subset of `Place`, not the full record: a
 * punch's own screen needs a name and how far off-centre the fix landed, nothing about
 * kind or radius.
 */
export interface PlaceRef {
  id: Guid;
  name: string;
  milesFromCentre: number;
}

/**
 * The two points a shift recorded, and how far apart they were.
 *
 * TWO POINTS, AND NOTHING HERE MAY IMPLY MORE. A punch records a coordinate where the
 * punch happened, once at each end. A browser cannot track in the background —
 * `navigator.geolocation` runs only while the page is open — so a trail is not merely
 * unbuilt, it is not buildable without a native app this product does not have. The clock
 * screen's promise, "a single stamp — you are not tracked while you work", stays literally
 * true with these on the wire.
 *
 * ⚠️ CARRIES A PLACE NAME NOW (ef-021 — `startPlace`/`endPlace`), resolved LIVE by the
 * server against the tenant's currently-active places (`PlaceResolver`), never stored on
 * the row. Archiving a place therefore falls every punch that used to resolve to it back
 * to a bare coordinate on its very next fetch, with nothing about the punch itself
 * re-saved. Both are independently nullable: a fix with no place inside its radius, or no
 * fix at all, resolves to `null` and the screen falls back to the raw coordinate.
 */
export interface PunchLocation {
  /** Where the shift STARTED. Always present when this object is. */
  startLat: number;
  startLon: number;
  /** The saved place whose radius contains the clock-IN coordinate, or null when none
   *  does — including a place that used to match but has since been archived. */
  startPlace: PlaceRef | null;
  /** Where it ENDED, or null — nullable independently of the start pair. A shift that
   *  began with a fix may end without one, and a running shift has no end at all. */
  endLat: number | null;
  endLon: number | null;
  /** Same resolution as `startPlace`, against the clock-OUT coordinate. Independently
   *  null: a shift may start inside a saved place and end with no fix at all. */
  endPlace: PlaceRef | null;
  /**
   * Miles between the two punches, or null when either end has no fix.
   *
   * NOT zero when unknown. "0.0 miles apart" renders as "clocked out in the same place",
   * which is a claim about a location nobody recorded — the more dangerous kind of wrong,
   * because it reads as a fact rather than as a gap.
   */
  milesApart: number | null;
}

/**
 * One recorded day — GET /api/demo/time/mine and /pending.
 *
 * Carries no wage, no rate and no employer-side detail of any kind, because an EMPLOYEE
 * login reads this shape and must learn nothing about the farm's books from it (see
 * TimeEntryResponse on the API).
 */
export interface TimeEntry {
  id: Guid;
  employeeId: Guid;
  employeeName: string;
  employeeNumber: string;
  source: TimeEntrySource;
  /** The DAY the work happened — `YYYY-MM-DD`, the only field payroll needs beside hours. */
  workDate: string;
  startedAt: string | null;
  endedAt: string | null;
  /** Null ONLY while a punch is still running — nobody knows the hours of a shift in
   *  progress. Not a zero, and the screen must not render it as one. */
  hours: number | null;
  /**
   * Where the punch happened, or null when nothing was recorded.
   *
   * ⚠️ THIS REPLACED A BOOLEAN on 2026-09-08. The field was `hasLocation`, and the
   * reasoning was that a worker's position is the most sensitive thing here and no screen
   * needed it. That was wrong in a specific way: "location stamped" tells a reader a
   * location EXISTS and never where, so capturing GPS and hiding it bought no privacy —
   * only an unanswered question. Either it is worth recording and worth reading, or it
   * should not be recorded.
   */
  location: PunchLocation | null;
  status: TimeEntryStatus;
  /** Why a row was rejected. Shown to the employee — a rejection with no reason is how a
   *  payroll dispute starts. */
  reviewNote: string | null;
  /**
   * What the WORKER said about this day, or null.
   *
   * The symmetric half of `reviewNote`, and until 0019 the channel only ran one way: a
   * rejection carried a reason and the worker had no way to give one first. Read by the
   * owner BEFORE the Approve and Reject buttons — a note read after the decision is a note
   * that changed nothing.
   *
   * Written once, when the day was recorded, and not editable afterwards.
   */
  workerNote: string | null;
  /**
   * Minutes the worker said they were not working, or null. Punch rows only.
   *
   * ⚠️ NOT deducted from `hours`, and no screen may imply it was. The hours beside it are
   * the clocked duration, end minus start; this is a separate fact the worker reported.
   * Null means not asked or not said — never zero, which claims they took none.
   *
   * Nothing rendering this may say "compliant", "required" or "entitled": the product has
   * taken no position on meal-break law. See 0020.
   */
  breakMinutes: number | null;
  /**
   * When the WORKER confirmed the hours of the period this day falls in, or null.
   *
   * ⚠️ NOT AN APPROVAL, and must never be rendered as one. The worker agreeing the hours
   * are right and the employer approving them are two different acts by two different
   * people, and the product keeps both. This is what puts signed / not-signed in front of
   * the farmer BEFORE they decide, which is the whole reason to build signing rather than
   * print a line on paper.
   *
   * Null on an open shift: a running shift has no hours to have agreed with.
   */
  signedAt: string | null;
  /** The signature exists but the days it covered have changed since. Only meaningful
   *  when `signedAt` is set — an UNSIGNED row reads `false` too, so this is never on its
   *  own evidence that a row is signed and current. */
  signatureStale: boolean;
  /**
   * The phone's own clock, not the server's, was used for this row's punch time(s) — see
   * db/migrations/0025_offline_punch.sql. Not part of the signature: a worker signs that
   * the HOURS are right, and this is metadata about how the row arrived.
   */
  recordedOffline: boolean;
  /**
   * When the server actually received the offline claim, or null. Only meaningful
   * alongside `recordedOffline` — the gap between this and `endedAt` (or `startedAt` for a
   * shift still open) is the delay a farmer is being asked to trust rather than verify.
   */
  receivedAt: string | null;
  /**
   * ef-023 — how this day's hours are split across the farm's own job codes. `[]` means
   * unallocated, never `null` — there is always a real, always-valid answer (nothing
   * assigned yet), same convention `PunchLocation`'s nullable fields avoid for a
   * different reason. Resolved server-side from `time_entry_allocation` joined to
   * `job_code` (`TimeContracts.cs` `AllocationResponse`), so a screen never needs a
   * second lookup to render a code's name.
   */
  allocations: Allocation[];
}

/**
 * `job_code.kind`'s own CHECK constraint (db/migrations/0027_job_code.sql) —
 * DELIBERATELY DIFFERENT from `PlaceKind`, kept as its own union for the same
 * typo-is-a-compile-error reasoning. Mirrors `JobCodeEndpoints.AllowedKinds`.
 */
export type JobCodeKind = "field" | "livestock" | "shop" | "equipment" | "general" | "other";

/**
 * ef-022/ef-023 — one of the farm's own job codes: WHAT a day's hours were for, distinct
 * from `Place`, which answers WHERE. GET/POST/PATCH/DELETE `/api/demo/job-codes`.
 * Owner-only — an employee reaches one only indirectly, through the resolved
 * `allocations` on their own time entries (see `Allocation` below).
 */
export interface JobCode {
  id: Guid;
  code: string;
  name: string;
  kind: JobCodeKind;
  /** Null = active. Set once, by DELETE — there is no restore route
   *  (`JobCodeRepository.ArchiveAsync`'s own doc comment); archiving is one-way. */
  archivedAt: string | null;
  createdAt: string;
}

/** `POST /api/demo/job-codes`. `kind` is optional — the database itself defaults it
 *  ('general'), same convention as `CreatePlaceRequest.kind`. */
export interface CreateJobCodeRequest {
  code: string;
  name: string;
  kind?: JobCodeKind;
}

/** `PATCH /api/demo/job-codes/{id}`. Every field optional — whatever is omitted keeps
 *  its current value. A partial update, never a full replace. */
export interface UpdateJobCodeRequest {
  code?: string;
  name?: string;
  kind?: JobCodeKind;
}

/**
 * One slice of a day's hours assigned to a job code, as it stands on a `TimeEntry` or a
 * `PUT /time/{id}/allocations` response — carries the job code's own `code`/`name`
 * resolved, never a second id a screen would have to look up.
 */
export interface Allocation {
  jobCodeId: Guid;
  code: string;
  name: string;
  hours: number;
}

/** One row of `PUT /api/demo/time/{id}/allocations`'s request body. */
export interface AllocationItem {
  jobCodeId: Guid;
  hours: number;
}

/**
 * `PUT /api/demo/time/{id}/allocations`'s body — the WHOLE set this call replaces. An
 * empty list clears every allocation on the entry; this is PUT semantics, never a
 * partial merge (`TimeEndpoints.cs`'s own remark on `PutAllocationsRequest`).
 */
export interface PutAllocationsRequest {
  allocations: AllocationItem[];
}

/** How a signature was given. */
export type SignatureMethod = "drawn" | "typed" | "paper";

/**
 * What a worker signed.
 *
 * ⚠️ NEVER LABEL THIS "H-2A compliant" or "meets federal requirements". 20 CFR 655.122(j)
 * requires the employer to FURNISH a written statement of hours, rate and earnings; it
 * contains no signing requirement, and 655.167's retention rule adds none. Signed daily
 * records are ordinary farm-labour-contractor practice and the strongest evidence in a
 * Wage & Hour investigation — which is why the feature exists — but every string rendered
 * from this says only what happened: the worker confirmed these hours are correct, on this
 * date. Same discipline as "Projected, not filed" on the tax screens.
 */
export interface TimesheetSignature {
  signedAt: string;
  method: SignatureMethod;
  /** The name typed, for `method: "typed"`. Never defaulted from the employee record. */
  typedName: string | null;
  /** The period total AS IT STOOD when signed. Frozen — this figure is the evidence, and
   *  a screen must never quietly render `currentHours` in its place. */
  signedHours: number;
  /** How many distinct DATES the signed hours covered, frozen at the moment of signing. */
  signedDayCount: number;
  /**
   * The days this signature covered have changed since it was given: a day added, a day
   * removed, or an hours figure edited.
   *
   * An employer APPROVING or REJECTING a day does NOT set this — that is a different act
   * by a different person, and voiding the signature on it would mean the queue read
   * "signed" for about as long as it took to click Approve.
   */
  isStale: boolean;
  /** Today's total for the same period. Equal to `signedHours` unless `isStale`; sent
   *  alongside so the screen can show both rather than asking the reader to remember. */
  currentHours: number;
  /** Today's day count for the same period — distinct dates, matching `signedDayCount`, or
   *  the two figures the screen shows side by side would count different things. */
  currentDayCount: number;
}

/**
 * The fortnight being worked, and what this worker has recorded in it.
 *
 * The boundaries are the farm's REAL pay period — derived server-side from the same
 * first-Friday-then-every-14-days anchor the Form 941 card counts quarters with, and
 * inclusive at both ends. Not the `PAY_PERIOD_LABEL` fixture in payroll-fixtures.ts, and
 * not a rolling "last 14 days", which would move every morning so that no day was ever
 * covered by exactly one signature.
 */
export interface PayPeriod {
  /** `YYYY-MM-DD`, inclusive. */
  start: string;
  /** `YYYY-MM-DD`, inclusive — the payday, which is in its own period. */
  end: string;
  /** Hours recorded in the period right now, whatever their approval state. A signature
   *  is about the hours being right, not about the employer agreeing. */
  hours: number;
  /** How many distinct DATES those hours are spread across. Not a row count — several rows
   *  can share one date, so a worker who clocks out for lunch and back in still worked one
   *  day. It counted rows until 2026-09-10; see 0021. */
  dayCount: number;
  /** This worker's signature on this period, or null if they have not signed it. */
  signature: TimesheetSignature | null;
}

/** Everything the employee's own screen needs, in one call. */
export interface MyTime {
  /** This business's recording mode — decides which controls the phone offers. */
  mode: TimeEntryMode;
  /** The shift currently running, or null. Drives Clock in vs. Clock out. */
  openShift: TimeEntry | null;
  recent: TimeEntry[];
  /**
   * The period the sign card covers.
   *
   * ⚠️ ONLY THE CURRENT ONE. A worker cannot yet reach back and sign a fortnight that has
   * already closed — the case somebody off sick actually hits. Stated rather than hidden;
   * it needs a period picker.
   */
  period: PayPeriod;
}

/**
 * POST /api/demo/time/sign — the worker confirms a period's hours are right.
 *
 * NOTE WHAT IS ABSENT, in the spirit of the block heading above: the period, and the
 * hours. The server derives the fortnight from the payroll calendar and recomputes the
 * totals inside the write, so what gets frozen is what the database holds at that instant
 * — not what this screen was showing when the worker looked at it.
 */
export interface SignTimesheetRequest {
  /** Only `"drawn"` or `"typed"` from a worker. `"paper"` is an owner-side statement and
   *  is refused here. */
  method: Exclude<SignatureMethod, "paper">;
  /** Required for `"typed"`, and rejected for `"drawn"`. */
  typedName?: string;
  /** A PNG data URL, required for `"drawn"` and rejected for `"typed"`. */
  signatureImage?: string;
}

/**
 * Optional coordinates for a punch. Both nullable, and the whole body is optional: a
 * worker may decline, a phone may have no fix in a shed, and a punch must NEVER be
 * blocked by GPS — somebody who cannot clock in does not get paid.
 */
export interface PunchRequest {
  latitude?: number;
  longitude?: number;

  /** What the worker wants to say about the shift. Read on punch-OUT and ignored on
   *  punch-in — at the start of a shift there is nothing to explain yet. */
  workerNote?: string;

  /** Minutes not worked during the shift. Punch-out only. RECORDED, NEVER DEDUCTED —
   *  `hours` stays the clocked duration. Omitted means not said, never zero. */
  breakMinutes?: number;

  /**
   * ef-017 — present ONLY when this request is the outbox REPLAYING a punch that
   * could not reach the server the first time (`offline/punchOutbox.ts`). Never sent
   * on a live, first-attempt tap — see `TimeClock.tsx`'s `punch()`, which omits both
   * of these fields entirely on that path. Travels with `recordedAt` as a pair;
   * sending one without the other is refused (`TimeContracts.cs`
   * `ValidateOfflinePunchFields`). Mirrors the server's `PunchRequest.ClientPunchId`
   * (`Guid?`) — a client-generated id the server uses to make a replay idempotent.
   */
  clientPunchId?: string;

  /**
   * The PHONE's own clock at the moment of the tap, for an offline replay — never
   * re-derived at replay time, which would silently substitute "whenever the queue
   * finally drained" for "when the worker actually tapped". ISO 8601, always with a
   * `Z` offset (`Date.prototype.toISOString()`), so the server's `DateTime?` binder
   * reads it as UTC unambiguously. See `clientPunchId` above for why the two travel
   * together.
   */
  recordedAt?: string;
}

export interface PunchInResult {
  id: Guid;
  /** SERVER time, not the phone's. A device clock is the one input in this flow the
   *  employer cannot verify, and shift length is money. */
  startedAt: string;
}

export interface PunchOutResult {
  /**
   * ef-024 — the id of the row this punch-out just closed, so the allocation panel knows
   * what to PUT against. ABSENT when the shift closed but filed nothing (`recorded:
   * false` below) — a discarded 0.00h shift has no row to allocate. Present on the live,
   * offline-replay AND idempotent-replay paths alike (`TimeEndpoints.cs` punch-out).
   */
  id?: Guid;
  endedAt: string;
  hours: number;
  /**
   * False when the shift closed but filed nothing, because it recorded no time.
   *
   * Clocking in and straight back out used to send a 0.00 hrs claim to the farmer's
   * approval queue and tell the worker it had been "sent to your employer to approve".
   * The server now closes the shift and discards it (TimeEndpoints punch-out), and this
   * flag is what lets the screen say so rather than silently swallowing the tap.
   */
  recorded: boolean;
}

/** Hours for a day, for farms that want no clock at all. */
export interface ManualTimeRequest {
  /** `YYYY-MM-DD`. Refused if in the future or more than 14 days back. */
  workDate: string;
  hours: number;

  /** Optional. Whitespace-only is stored as nothing said, and anything over 500 characters
   *  is refused with the complaint on this field. */
  workerNote?: string;
}

/** An owner's decision on one submitted row. */
export interface ReviewTimeRequest {
  approved: boolean;
  /** Shown to the employee. Optional on an approval; the UI insists on one for a
   *  rejection, which is a product rule rather than a server one. */
  note?: string;
}

/** Roll approved hours for a pay period onto the roster. */
export interface ApplyHoursRequest {
  from: string;
  to: string;
}

export interface ApplyHoursResult {
  employeesUpdated: number;
  /** ef-034 — the run(s) this apply wrote or refreshed, usually one. See PayRun below. */
  runs: PayRun[];
}

/**
 * One employee's line on a pay run — hours, plus this line's own money once the owning
 * run is finalized. ef-035: `GET /runs` and `POST /apply` now carry gross/FICA/
 * withholding/net per employee too, not just the four run-level totals on `PayRun` —
 * null until finalized, all six together (`pay_run_line_money_all_or_nothing`, 0030),
 * matching what a live `POST /finalize` (`PayRunFinalizeLine` below) returns once. A
 * reloaded finalized run now shows this same per-employee breakdown, not only the
 * run-level totals.
 */
export interface PayRunLine {
  /** ef-047-4 — this line's own row id, the addressable target of
   *  `POST/GET /payroll/runs/{runId}/lines/{lineId}/print`. */
  id: string;
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  hours: number;
  /** Distinct DATES those hours came from, not a row count — same discipline as
   *  `HoursReportLine.approvedDays`. */
  dayCount: number;
  /** Null until the owning run is finalized, all six together. */
  gross: number | null;
  ficaEmployee: number | null;
  ficaEmployer: number | null;
  federalWh: number | null;
  stateWh: number | null;
  net: number | null;
  /** ef-060 — the piece-ticket part of `gross` (gross = hourly/salary pay + piecePay). 0 on a
   *  draft line and on a line with no piece pay. */
  piecePay: number;
}

/**
 * One pay run — approved hours for one entity, one period, applied once and kept as
 * history. `PayRunFinalizeEndpoints`/`TimeEndpoints` (`ApplyApprovedHoursAsync`,
 * `GetPayRunsAsync`) are the source; ef-034 is the first screen to read it.
 */
export interface PayRun {
  id: string;
  entityId: string;
  /** `YYYY-MM-DD`, inclusive. */
  periodStart: string;
  periodEnd: string;
  /** When this run was most recently applied — refreshed, never left standing, on a
   *  re-apply of the same period. */
  appliedAt: string;
  totalHours: number;
  employeeCount: number;
  lines: PayRunLine[];
  /** All four null until finalized (`pay_run_money_all_or_nothing`, 0030) — never
   *  partially set. */
  grossTotal: number | null;
  employerTaxTotal: number | null;
  withholdingTotal: number | null;
  netTotal: number | null;
  /** When this run was finalized, or null while it is still a draft. */
  finalizedAt: string | null;
  /** ef-030 — when this run was voided, or null while it stands. Optional (not just
   *  `| null` alone): a pre-ef-030 API response sends no property at all, and reading
   *  `undefined` the same as "not voided" keeps an old cached response from crashing this
   *  screen rather than requiring a hard refresh. */
  voidedAt?: string | null;
  /** Required, 1-200 characters, once `voidedAt` is set. Null/undefined together with it. */
  voidReason?: string | null;
  /** ef-033 — the single balanced ledger entry this run's money was posted as, or null/
   *  undefined while it stands unposted. Optional (not just `| null` alone): a pre-ef-033
   *  API response sends no property at all, and reading `undefined` the same as "not
   *  posted" keeps an old cached response from crashing this screen rather than requiring
   *  a hard refresh (same convention as `voidedAt` above). Set together with `postedAt` —
   *  `PayRunPostRepository.WritePostAsync`'s own transaction writes both in one commit. */
  postedEntryId?: string | null;
  /** When this run was posted, or null/undefined while it stands unposted. */
  postedAt?: string | null;
  /** ef-036 — the ledger entry that reverses `postedEntryId`, or null/undefined unless
   *  this run is BOTH voided and posted. Optional, same pre-this-job-API convention as
   *  `postedEntryId`/`voidedAt` above. `postedEntryId`/`postedAt` are untouched by a
   *  reversal — both entries stand on file. */
  reversalEntryId?: string | null;
}

/** `GET /api/demo/time/runs` — the payroll history. */
export interface GetPayRunsResponse {
  runs: PayRun[];
}

/** `PATCH /api/demo/employees/{id}/pay` — ef-027. */
export interface UpdateEmployeePayRequest {
  /** "hourly" or "salary" — `employee.pay_basis`'s own CHECK constraint. */
  payBasis: string;
  /** Dollars per hour, or null to clear it. `>= 0` when supplied. */
  hourlyRate: number | null;
}

/** One employee's computed figures on a finalized run — `POST /finalize`'s per-line
 *  detail. See the note on `PayRunLine` above for why this is a SEPARATE, narrower-lived
 *  shape rather than an extension of it. */
export interface PayRunFinalizeLine {
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  gross: number;
  ficaEmployee: number;
  ficaEmployer: number;
  federalWh: number;
  stateWh: number;
  net: number;
}

/** ef-060 — what the finalize response actually carries per line: `PayRunFinalizeLine` + the
 *  piece-ticket part of `gross`. A subtype (not a new required field on the base) so `PayRuns.tsx`'s
 *  own line-to-finalize-line mapping keeps compiling until ef-060-8 uses it. */
export interface PayRunFinalizeLineWithPiece extends PayRunFinalizeLine {
  piecePay: number;
}

/**
 * `POST /api/demo/payroll/runs/{id}/finalize`'s response — the run's four totals, every
 * line's figures, and `assumptions`: what was NOT computed for real (per-state
 * withholding, year-to-date wages, a real W-4), said out loud rather than presented as a
 * real-looking figure that was actually a placeholder.
 */
export interface PayRunFinalizeResult {
  id: string;
  entityId: string;
  periodStart: string;
  periodEnd: string;
  grossTotal: number;
  employerTaxTotal: number;
  withholdingTotal: number;
  netTotal: number;
  finalizedAt: string;
  finalizedBy: string;
  /** ef-060 — SUM of the lines' piece pay (already inside `grossTotal`). */
  piecePayTotal: number;
  lines: PayRunFinalizeLineWithPiece[];
  assumptions: string[];
}

/** `POST /api/demo/payroll/runs/{id}/void` — ef-030. */
export interface VoidPayRunRequest {
  /** Required, 1-200 characters — why this run is being voided. */
  reason: string;
}

/**
 * `POST /api/demo/payroll/runs/{id}/void`'s response. Deliberately narrow — a void
 * computes no NEW money, so there is no per-line breakdown here the way
 * `PayRunFinalizeResult` carries one.
 */
export interface VoidPayRunResult {
  id: string;
  voidedAt: string;
  voidedBy: string;
  voidReason: string;
  /** ef-036 — the ledger entry that reverses this run's `postedEntryId`, or null/
   *  undefined when this run was never posted (nothing to reverse). */
  reversalEntryId?: string | null;
}

/**
 * `POST /api/demo/payroll/runs/{id}/post`'s success (201) response —
 * `PayRunPostEndpoints.cs`'s own `PayRunPostResponse`. Deliberately narrow — a post
 * computes no new money (every dollar was already fixed at finalize time); this only
 * names the one ledger entry that run's money became.
 */
export interface PayRunPostResult {
  entryId: string;
  postedAt: string;
}

/**
 * `POST`/`GET /api/demo/payroll/runs/{runId}/lines/{lineId}/print`'s response — the
 * render payload for one printed check. ef-047-4,
 * db/migrations/0067_pay_run_line_check_print.sql.
 */
export interface PayRunPrintResult {
  lineId: string;
  runId: string;
  checkNumber: number;
  printedAt: string;
  payeeName: string;
  /** This line's own `net` (0030) — a check is written for what the employee is
   *  actually paid, never gross. */
  amount: number;
  /** The standard check-writing dollars-and-cents sentence — computed server-side by
   *  `AmountInWordsFormatter`, never re-derived here. */
  amountInWords: string;
  periodStart: string;
  periodEnd: string;
  /** ef-060 — the stub's earnings block: gross = hourlyPay + piecePay. `amount` stays the net. */
  gross: number;
  hourlyPay: number;
  piecePay: number;
  /** True when this check's CURRENT print has since been voided. */
  voided: boolean;
  voidedAt?: string | null;
  voidReason?: string | null;
}

/** `POST /api/demo/payroll/runs/{runId}/lines/{lineId}/void` — ef-047-4. */
export interface VoidCheckRequest {
  /** Required, 1-200 characters — why this check is being voided. */
  voidReason: string;
}

/**
 * `POST /api/demo/payroll/runs/{runId}/lines/{lineId}/void`'s response. Deliberately
 * narrow, same posture as `VoidPayRunResult` — a void computes no new money.
 */
export interface VoidCheckResult {
  lineId: string;
  voidedAt: string;
  voidedBy: string;
  voidReason: string;
}

/**
 * One person's line of the owner's hours report — GET /api/demo/time/report.
 *
 * APPROVED rows only. Submitted time is a claim and rejected time is a claim that was
 * refused; neither is an hour the farm has agreed to pay, and a total that summed all
 * three would be a larger, plausible number nothing downstream would ever contradict.
 *
 * `approvedDays: 0` is a MEASUREMENT, not a gap. Somebody with no approved days in the
 * window is still on the roster and still gets a line, because "you have approved nothing
 * for this person" is an answer the farmer needs — an absent row would read as though
 * they had left.
 */
export interface HoursReportLine {
  employeeId: string;
  employeeNumber: string;
  employeeName: string;
  /** Never overtime-adjusted, and never to be rendered as though it were — this product
   *  has taken no position on agricultural overtime. */
  approvedHours: number;
  /** Distinct DATES, not rows. Two shifts on one date are one day — the schema permits
   *  several rows per date (only ONE OPEN shift is unique), and counting rows rendered
   *  "1071 days" inside a fortnight the first time this reached a screen. */
  approvedDays: number;
}

/**
 * The owner's hours report: the window the SERVER actually reported, and a line per person.
 *
 * The window is echoed back because it may not be the one asked for — with no from/to the
 * server derives the current pay period, the same one the worker's screen signs against.
 * A client that printed its own guess beside these figures would eventually print a
 * different fortnight than the numbers came from.
 *
 * Not a payroll ledger: no pay_run table exists. It reports what is in time_entry for the
 * queried range, and screens rendering it must not imply it is filed or official.
 */
export interface HoursReport {
  start: string;
  end: string;
  lines: HoursReportLine[];
}

/**
 * One line of the owner's labour-by-job-code report — GET /api/demo/reports/labour.
 * ef-038-1.
 *
 * `jobCodeId`/`code` are BOTH null together, exactly once — the synthetic `Unallocated`
 * line, for approved hours that carry no allocation at all. Every real job-code line
 * carries both.
 *
 * `hours` counts EVERY approved hour under this code, hourly and salaried employees
 * alike — a roster fact, not a cost. `estimatedCost` is the hourly-only share, priced at
 * each contributing employee's own rate, and is `null` (never `0`) when nobody hourly
 * worked this code: a salaried-only line has hours to show and no honest per-hour price
 * to put beside them.
 */
export interface LabourReportLine {
  jobCodeId: string | null;
  code: string | null;
  name: string;
  hours: number;
  estimatedCost: number | null;
}

/**
 * The owner's labour-by-job-code report: the window the SERVER actually reported, and
 * one line per job code with approved allocated hours in it, plus `Unallocated` when it
 * applies. Not a payroll ledger — same disclosure as `HoursReport` above.
 */
export interface LabourReport {
  start: string;
  end: string;
  lines: LabourReportLine[];
}

/**
 * The bookkeeper's timesheet export — GET /api/demo/time/export.
 *
 * `delimitedFile` is the server writer's bytes verbatim; a client turns it into a Blob and
 * adds a filename, and does nothing else to it. Never re-serialise from rows — that would
 * be a second implementation of the file format, free to disagree with the first about a
 * comma in somebody's name.
 *
 * EVERY STATUS is in this file, unlike the hours report, which totals approved time only.
 * Any screen offering both must say which is which.
 *
 * `rowCount` exists so a header-only file reads as "this window had no time in it" rather
 * than as a failure — an empty download and a broken one are otherwise the same silence.
 */
export interface TimesheetExport {
  start: string;
  end: string;
  rowCount: number;
  delimitedFile: string;
  delimitedFileName: string;
}

/** Request body for POST /api/payroll/annual-futa (IRS Form 940). */
export interface AnnualFutaRequest {
  payPeriodsToProject: number;
  employees: PayrollRunEmployeeRequest[];
}

/**
 * Form 940 Part 2 lines. The ANNUAL federal unemployment return.
 *
 * The one form BOTH businesses file. A farm's agricultural workers go on a 943 and a
 * non-farm business's on a quarterly 941, but FUTA does not split that way — every
 * employer files one 940 a year. H-2A wages are exempt from it, exactly as they are from
 * FICA, which is why the same four people drop out of this return too.
 */
export interface Form940Lines {
  totalPayments: number;
  paymentsExemptFromFuta: number;
  paymentsExcessOfWageBase: number;
  subtotal: number;
  totalTaxableFutaWages: number;
  futaTaxBeforeAdjustments: number;
  /** 0.060 — the gross FUTA rate. */
  grossRate: number;
  /** 0.054 — the maximum state-unemployment credit. */
  maxCredit: number;
  /** 0.006 — gross less credit, and the exact line-8 multiplier. */
  effectiveRate: number;
  futaExemptEmployeeCount: number;
}

export interface AnnualFutaForm {
  basis: string;
  taxYear: number;
  payPeriodsProjected: number;
  form940: Form940Lines;
  excluded: YearEndEmployeeError[];
}

/**
 * One row of the owner-facing access list — GET /api/auth/users.
 *
 * Mirrors TenantUserResponse in AuthContracts.cs. Deliberately carries no password hash,
 * no session token and no employee id: the screen names a person and offers one control,
 * and anything more would be a wider disclosure than the feature needs on a screen an
 * owner may well leave open on a laptop at a trade show.
 */
export interface TenantUser {
  id: string;
  email: string;
  /** "owner" or "employee", read from the database — never from the request. */
  role: string;
  /** The employee this login speaks for; null for an owner login. */
  employeeName: string | null;
  createdAt: string;
  /** When the login was switched off; null while it is live. */
  revokedAt: string | null;
  /** Sent by the server rather than derived from `revokedAt`, so client and server cannot
   *  disagree about which direction the timestamp means. */
  isActive: boolean;
  /** Whether this is the caller's own login. The screen draws no revoke control for it —
   *  but the server refuses it regardless; this only shapes the UI. */
  isSelf: boolean;
}

/**
 * A standing instruction to post the same category-account entry on a repeating
 * schedule (ef-042-4) — /api/demo/books/recurring/*.
 * POST .../post-now fires it through the SAME write path an ordinary Check/Deposit
 * already uses; see db/migrations/0046_recurring_transaction.sql's own header for why
 * accountId is always the CATEGORY leg (never the instrument the register itself is
 * scoped to) and why there is no separate registerAccountId. Mirrors
 * RecurringTemplateResponse field-for-field.
 */
export interface RecurringTemplate {
  id: Guid;
  entityId: Guid;
  accountId: Guid;
  amount: number;
  memo: string | null;
  frequency: RecurringFrequency;
  nextDueOn: string;
  isActive: boolean;
  createdAt: string;
}

/** recurring_transaction.frequency's own CHECK constraint. */
export type RecurringFrequency = "weekly" | "biweekly" | "monthly" | "quarterly";

/** POST /api/demo/books/recurring. accountId must name a category (non-Asset)
 *  account — an instrument is refused with a 400 naming accountId. */
export interface CreateRecurringRequest {
  entityId: Guid;
  accountId: Guid;
  amount: number;
  memo?: string | null;
  frequency: RecurringFrequency;
  nextDueOn: string;
}

/** PATCH /api/demo/books/recurring/{id}. Every field optional — whatever is omitted
 *  keeps its current server-side value. A partial update, never a full replace. */
export interface UpdateRecurringRequest {
  isActive?: boolean;
  amount?: number;
  memo?: string | null;
  frequency?: RecurringFrequency;
  nextDueOn?: string;
}

/** POST /api/demo/books/recurring/{id}/post-now — the new entry's id and the
 *  template's nextDueOn AFTER advancing, so the caller's next render shows the
 *  post-advance date without a second GET. NOT idempotent — see postRecurringNow's
 *  own remarks in client.ts. */
export interface PostRecurringNowResponse {
  entryId: Guid;
  nextDueOn: string;
}

/**
 * GET /api/demo/vendor-payment-history — which cards each vendor has been paid with, and
 * when each was first used. Beat 3's history, served from the ledger because the client used
 * to infer it from the twenty-five-row activity feed and lost it after a busy morning (see
 * lib/anomaly.ts).
 */
export interface VendorPaymentHistoryRow {
  vendorId: string;
  paymentMethodId: string;
  firstUsedAt: string;
}

/* ---------------------------------------------------------------------------
 * Schedule & time off — /api/demo/schedule/*, /api/demo/time-off/* (ef-040-2/3/4)
 *
 * Verified against src/EasyFarm.Api/Demo/ScheduleContracts.cs. Shared by both the owner's
 * CRUD + review-queue screens (ef-040-3: Schedule.tsx, TimeOff.tsx) and the employee's
 * read-only `/schedule/mine` + `/time-off/mine` + submit-a-request screens (ef-040-4:
 * MyShifts.tsx, TimeOffRequests.tsx) — one wire shape, read and written by both roles.
 * ------------------------------------------------------------------------- */

/** `shift.status`'s own CHECK constraint (db/migrations/0037), defaulting to `draft`
 *  server-side on create — a shift is built before it is shown to the worker it names. */
export type ShiftStatus = "draft" | "published" | "cancelled";

/**
 * GET/POST/PATCH/DELETE `/api/demo/schedule/shifts`, GET `/api/demo/schedule/mine` — a
 * planned work slot. Distinct from `TimeEntry`: this is WHAT IS PLANNED, never what was
 * actually worked — see ScheduleEndpoints.cs's own remarks. Mirrors `ShiftResponse`
 * field-for-field.
 */
export interface Shift {
  id: Guid;
  employeeId: Guid;
  employeeName: string;
  scheduledStart: string;
  scheduledEnd: string;
  place: string | null;
  note: string | null;
  status: ShiftStatus;
  createdAt: string;
}

/** `POST /api/demo/schedule/shifts`. `employeeId` names WHO the shift is for — never the
 *  caller, who has no employee id of their own (this is an owner-only route). */
export interface CreateShiftRequest {
  employeeId: Guid;
  scheduledStart: string;
  scheduledEnd: string;
  place?: string | null;
  note?: string | null;
}

/** `PATCH /api/demo/schedule/shifts/{id}`. Every field optional — whatever is omitted
 *  keeps its current server-side value. A partial update, never a full replace. */
export interface UpdateShiftRequest {
  status?: ShiftStatus;
  scheduledStart?: string;
  scheduledEnd?: string;
  place?: string | null;
  note?: string | null;
}

/** `time_off_request.kind`'s own CHECK constraint. */
export type TimeOffKind = "vacation" | "sick" | "personal" | "unpaid" | "other";

/** `time_off_request.status`'s own CHECK constraint. */
export type TimeOffStatus = "pending" | "approved" | "denied";

/**
 * GET `/api/demo/time-off/mine`, GET `/api/demo/time-off/pending`, GET `/api/demo/time-off`
 * — a worker's leave request and, once decided, the owner's own outcome on it.
 * `reviewNote` (0038) is the OWNER's own text on the decision, distinct from `reason` (the
 * REQUESTER's own) — the two are never conflated on screen. `recordedOffline`/`receivedAt`
 * (ef-040-5, db/migrations/0039_time_off_offline.sql) mirror `PunchInResult`'s own
 * provenance fields — see that migration's header. Mirrors `TimeOffResponse` field-for-field.
 */
export interface TimeOffRequest {
  id: Guid;
  employeeId: Guid;
  employeeName: string;
  startDate: string;
  endDate: string;
  kind: TimeOffKind;
  hoursRequested: number;
  status: TimeOffStatus;
  reviewedBy: Guid | null;
  reviewedAt: string | null;
  reason: string | null;
  reviewNote: string | null;
  /** The phone's own clock was used for `createdAt` instead of the server's clock at
   *  request time. See db/migrations/0039_time_off_offline.sql. */
  recordedOffline: boolean;
  /** When the server actually received an offline claim. `null` unless `recordedOffline`. */
  receivedAt: string | null;
  createdAt: string;
}

/**
 * POST /api/demo/time-off — ef-040-4. Deliberately carries no `employeeId`: the session
 * names the requester, same discipline as every other employee-scoped write in this file
 * (`ManualTimeRequest`, `PunchRequest`). `kind` omitted defaults server-side to `"other"`
 * (`ScheduleEndpoints.DefaultTimeOffKind`) — this screen has no kind picker yet, so it is
 * simply never sent.
 *
 * `clientRequestId`/`recordedAt` (ef-040-5) are the offline-replay pair — present ONLY
 * when `offline/timeOffOutbox.ts` is replaying a submission that could not reach the
 * server the first time, never on a live, first attempt. Travel together; sending one
 * without the other is refused (`ScheduleContracts.cs` `SubmitTimeOffRequest`'s own
 * remarks). Mirrors `PunchRequest.ClientPunchId`/`RecordedAt` exactly.
 */
export interface SubmitTimeOffRequest {
  startDate: string;
  endDate: string;
  hoursRequested: number;
  reason?: string;
  clientRequestId?: string;
  recordedAt?: string;
}

/**
 * `POST /api/demo/time-off/{id}/review`. `note` is persisted as the OWNER's own
 * `review_note` — the API itself allows a bare approval with no note, but this screen
 * REQUIRES one on a denial (ADR 0009 §4 / TimeApprovalQueue's own precedent: "a
 * rejection must carry a reason").
 */
export interface ReviewTimeOffRequest {
  approved: boolean;
  note?: string;
}

/* ---------------------------------------------------------------------------
 * Livestock — /api/demo/livestock/* (ef-041-3/-4/-5)
 *
 * Verified against src/EasyFarm.Api/Demo/LivestockContracts.cs and the new
 * LivestockHistoryEndpoints.cs (ef-041-5's own single API addition — the read-only
 * status-history route). Owner-only, full stop: this wave has no employee-facing
 * livestock surface at all (LivestockEndpoints.cs's own remarks).
 * ------------------------------------------------------------------------- */

/** `animal.status`'s own CHECK constraint (db/migrations/0040), reused verbatim by
 *  `animal_status_change.from_status`/`to_status` (0042) rather than a second list this
 *  file would have to keep in step with the schema by hand. */
export type AnimalStatus =
  | "active"
  | "sold"
  | "deceased"
  | "culled"
  // ef-059 (db/migrations/0084): the three codes a dated status change adds.
  | "butchered"
  | "transferred"
  | "lost";

/**
 * One row of `GET /api/demo/books/register` — ef-042-1. Mirrors
 * `RegisterEntryResponse` field-for-field.
 */
export interface RegisterEntry {
  id: Guid;
  occurredOn: string; // "YYYY-MM-DD"
  /** Deposit | Check | Transfer | Mixed | Uncategorized — a plain string, not a closed
   *  union: Mixed/Uncategorized are read-side admissions that no single honest label
   *  applies (see `red`), not values this client should switch on exhaustively. */
  type: string;
  /** Signed for this account's own balance. Never re-derive the sign from `type`. */
  amount: number;
  /** This account's balance through and including this row, oldest-row-first
   *  cumulative — stable across pages of the same cursor-paginated fetch. */
  runningBalance: number;
  /**
   * The raw `entry.source` column value — deliberately its OWN string, NOT the shared
   * `EntrySource` union above. A hand-seeded transfer/split posts with
   * `source = 'payroll'`, which is not a member of `EntrySource`.
   */
  source: string;
  memo: string | null;
  /** The vendor/payee this entry's `vendor_id` resolves to, already a name — null exactly
   *  when the entry carries no vendor at all (most rows; `PostRegisterEntryRequest.vendorId`
   *  is optional). */
  vendorName: string | null;
  /** The other leg's account name(s), comma-joined for a split. Null exactly when `red`
   *  is true for the "no other leg" reason. */
  otherAccountName: string | null;
  /** True when this row's entry cannot be taken at face value — unbalanced, mixed-kind
   *  other legs, or no other leg at all. */
  red: boolean;
  /** ADR 0010 §2 — `entry_line.reconciled_on IS NOT NULL`. ef-056-0a anchor (frozen
   *  field name); ef-056-3's server side starts actually varying this per row. */
  cleared: boolean;
  /** ADR 0010 §2 — the register account's OWN `entry_line.id` for this row's entry
   *  (never `id` above, which is the ENTRY's id — the two differ on every split). This is
   *  the id `POST /api/demo/books/register/{entryLineId}/cleared` (`demoApi.setEntryLineCleared`)
   *  targets; the checkbox has nothing valid to send without it. ef-056-3-LEAD anchor
   *  (Spec Architect amendment, frozen field name): added after the ef-056-3 builder
   *  correctly reported this field was missing from the read contract. */
  entryLineId: Guid;
  /** ef-062 anchor (FROZEN field names) - the "print this check" flag (`entry_line.print_to_check`). */
  printToCheck: boolean;
  /** ef-062 anchor (FROZEN) - the line's LATEST register check (printed, or voided awaiting a reprint);
   *  null when none was ever printed. */
  check: RegisterCheckInfo | null;
  /** ef-064-7 anchor (FROZEN) - the "how many" typed on this entry: the `entry_line.quantity` of the entry's OTHER leg
   *  (the category line, not the register account's own); null when none, and always null for a split (read those per line
   *  from the GL detail). 3 decimals. */
  quantity: number | null;
  /** ef-064-7 anchor (FROZEN) - the unit of `quantity` (one of `REGISTER_QUANTITY_UNITS`); null exactly when `quantity` is. */
  unit: string | null;
  /** ef-064-7 anchor (FROZEN) - true when the entry has ONE other leg, so `PUT .../register/{entryLineId}/quantity` can set
   *  this row's quantity; false for a split (two or more other legs) or an entry with no other leg. */
  quantityEditable: boolean;
}

/**
 * ef-062 anchor (FROZEN) - register check printing wire types. Mirror `RegisterCheckContracts.cs`
 * field-for-field. A "check" is a register payment (a credit line on an asset account) with a payee whose
 * `printToCheck` flag is on; numbers come from ONE per-entity sequence shared with payroll checks.
 */
export interface RegisterCheckInfo {
  checkNumber: number;
  printedAt: string;
  voided: boolean;
  voidedAt: string | null;
  voidReason: string | null;
}

/** `POST .../print-check` (201) and `GET .../check` (200): one printed check's render payload. */
export interface RegisterCheck {
  entryLineId: Guid;
  checkNumber: number;
  printedAt: string;
  payeeName: string;
  /** The register line's own amount - always positive. */
  amount: number;
  /** Computed server-side by `AmountInWordsFormatter` - never re-derived here. */
  amountInWords: string;
  /** "YYYY-MM-DD" - the date on the check (the entry's date). */
  occurredOn: string;
  memo: string | null;
  voided: boolean;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface SetPrintToCheckResult {
  entryLineId: Guid;
  printToCheck: boolean;
}

export interface VoidRegisterCheckResult {
  entryLineId: Guid;
  checkNumber: number;
  voidedAt: string;
  voidReason: string;
}

/** One flagged, never-printed payment waiting for a check. */
export interface UnprintedCheckItem {
  entryLineId: Guid;
  occurredOn: string;
  payeeName: string;
  amount: number;
}

/** `GET .../unprinted-checks`: `count` and `total` cover ALL waiting payments (flagged, never printed, with a payee);
 *  `items` is the first 200 in print order; `unprintable` counts flagged ones with no payee (the batch refuses with 409
 *  while it is above zero). */
export interface UnprintedChecks {
  count: number;
  total: number;
  unprintable: number;
  items: UnprintedCheckItem[];
}

export interface PrintedCheckItem {
  entryLineId: Guid;
  checkNumber: number;
  occurredOn: string;
  payeeName: string;
  amount: number;
}

/** `POST .../print-unprinted-checks`: `printed` is 0 on a rerun; `remaining` > 0 when more than 200 were waiting. */
export interface PrintUnprintedChecksResult {
  printed: number;
  remaining: number;
  checks: PrintedCheckItem[];
}

/**
 * GET/POST/PATCH/DELETE `/api/demo/livestock/herds` — a top-level grouping. Mirrors
 * `HerdResponse` field-for-field.
 */
export interface Herd {
  id: Guid;
  entityId: Guid;
  name: string;
  breed: string | null;
  /** ef-042-0b — null = active. Set by `POST .../archive`, cleared by `POST
   *  .../restore`. Same shape as `Place.archivedAt`. */
  archivedAt: string | null;
  createdAt: string;
}

/**
 * POST /api/demo/books/register — ef-042-2. Two mutually exclusive shapes on one body,
 * distinguished by whether `lines` carries anything. Mirrors `PostRegisterEntryRequest`
 * field-for-field (EasyFarm.Api.Demo).
 *
 * Simple (`lines` empty/absent): a Check or Deposit — `categoryAccountId` + `amount`,
 * posted through the existing single-account shim. Allowed ONLY when
 * `registerAccountId` is the tenant's own default cash account.
 *
 * Explicit (`lines` non-empty): a Transfer or a split. Every line is sent verbatim —
 * including the register account's own leg — and `registerAccountId` is what the
 * resulting entry's header account is always set to, regardless of which line(s) name
 * it. No balancing arithmetic happens client-side; an unbalanced `lines` set is refused
 * by the server, atomically.
 */
export interface PostRegisterEntryRequest {
  registerAccountId: Guid;
  /** Simple shape only. */
  categoryAccountId?: Guid;
  /** Simple shape only. */
  amount?: number;
  /** Explicit shape only: every posting line, verbatim. */
  lines?: { accountId: Guid; side: "debit" | "credit"; amount: number; quantity?: number; unit?: string }[];
  /** Optional, both shapes — trimmed and capped at 200 characters server-side
   *  (`BooksRegisterEndpoints.MaxMemoLength`). */
  memo?: string;
  /** Optional, both shapes — the payee/vendor this entry is against. Resolved against
   *  this tenant server-side; an id that does not resolve is a 400 naming `vendorId`. */
  vendorId?: Guid;
  /** Optional, both shapes — ef-053-0b. ISO `YYYY-MM-DD`; omitted/undefined defaults to
   *  today server-side. Refused (400 naming `occurredOn`) when in the future or more than
   *  `TimeEndpoints.MaxBackdateDays` (14) days back. */
  occurredOn?: string;
  /** ef-064-7 anchor (FROZEN) - Simple shape only: how many, written to the category leg. Needs `unit` (400 naming `unit`
   *  without it); above 0, at most 3 decimals. On an Explicit body put it on the line it belongs to (`lines[i].quantity`). */
  quantity?: number;
  /** ef-064-7 anchor (FROZEN) - Simple shape only: one of `REGISTER_QUANTITY_UNITS`. */
  unit?: string;
}

/** ef-064-7 anchor (FROZEN) - `PUT /api/demo/books/register/{entryLineId}/quantity` body: both null (or both omitted) CLEARS. */
export interface SetRegisterQuantityRequest {
  quantity: number | null;
  unit: string | null;
}

/** ef-064-7 anchor (FROZEN) - the 200 of that PUT: the register line addressed and what the entry's other leg now says. */
export interface SetRegisterQuantityResult {
  entryLineId: Guid;
  quantity: number | null;
  unit: string | null;
}

/**
 * GET/POST/PATCH/DELETE `/api/demo/livestock/groups` — a finer working group inside a
 * herd. `herdId` is optional — a group can exist before it is assigned to a herd (0040's
 * own header). Mirrors `AnimalGroupResponse` field-for-field.
 */
export interface AnimalGroup {
  id: Guid;
  entityId: Guid;
  herdId: Guid | null;
  name: string;
  createdAt: string;
}

/**
 * GET/POST/PATCH/DELETE `/api/demo/livestock/animals` — one physical animal. Carries NO
 * `name` field — `tag` (the ear tag / physical id, NOT unique — 0040's own header) is the
 * only identifying field this contract exposes today; see `AnimalResponse`'s own remarks
 * in `LivestockContracts.cs` for why a `name` column existing in the database (0042) does
 * not mean this wire shape carries one yet. Mirrors `AnimalResponse` field-for-field.
 */
export interface Animal {
  id: Guid;
  entityId: Guid;
  herdId: Guid | null;
  groupId: Guid | null;
  damId: Guid | null;
  sireId: Guid | null;
  tag: string;
  sex: string | null;
  breed: string | null;
  birthDate: string | null;
  status: AnimalStatus;
  weightCurrent: number | null;
  createdAt: string;
  // ---- ef-059 anchor (frozen; mirrors `AnimalResponse`) ----
  brand: string | null;
  color: string | null;
  /** Plain text, not a foreign key (ef-059 decision D1). */
  ownerName: string | null;
  notes: string | null;
  twin: boolean;
  /** Non-blocking findings about the write that produced THIS response; `[]` on every read. */
  warnings: AnimalWarning[];
}

/** ef-059 — one non-blocking finding on an animal write. `duplicate_tag`: another ACTIVE
 *  animal in the same herd already carries this tag (trimmed, case-insensitive). */
export interface AnimalWarning {
  code: string;
  message: string;
  animalIds: Guid[];
}

/** `POST /api/demo/livestock/animals`. `tag` is required; every relational field is
 *  optional (0040's own header) but, when supplied, must resolve on this tenant and share
 *  this same `entityId` (`LivestockRepository.CreateAnimalAsync`). */
export interface CreateAnimalRequest {
  entityId: Guid;
  tag: string;
  herdId?: Guid | null;
  groupId?: Guid | null;
  damId?: Guid | null;
  sireId?: Guid | null;
  sex?: string | null;
  breed?: string | null;
  birthDate?: string | null;
  weightCurrent?: number | null;
  // ---- ef-059 anchor ----
  brand?: string | null;
  color?: string | null;
  ownerName?: string | null;
  notes?: string | null;
  twin?: boolean | null;
}

/** `PATCH /api/demo/livestock/animals/{id}`. Every field optional — whatever is omitted
 *  keeps its current server-side value (COALESCE). Carries NO `status` member on purpose:
 *  a status change is only ever written by `POST .../status`, which also writes the audit
 *  row — a bare PATCH here would let a status change through with no record of who did it
 *  or when. */
export interface UpdateAnimalRequest {
  herdId?: Guid | null;
  groupId?: Guid | null;
  damId?: Guid | null;
  sireId?: Guid | null;
  sex?: string | null;
  breed?: string | null;
  birthDate?: string | null;
  weightCurrent?: number | null;
  // ---- ef-059 anchor. For these five fields omitted/null = unchanged and a blank string
  // ("" or whitespace) CLEARS a text field; `twin: false` sets false. ----
  brand?: string | null;
  color?: string | null;
  ownerName?: string | null;
  notes?: string | null;
  twin?: boolean | null;
}

/** `POST /api/demo/livestock/animals/{id}/status`. A repeat transition to the animal's
 *  OWN current status is refused server-side (409), not silently re-logged — see
 *  `useLivestock.ts`'s own `changeAnimalStatus`. */
export interface ChangeAnimalStatusRequest {
  toStatus: AnimalStatus;
  reason?: string | null;
  /** ef-059 — `YYYY-MM-DD`, the day the change takes effect (the animal stops being counted
   *  on this day). Omitted = today. Never in the future, never before the birth date. */
  effectiveOn?: string | null;
}

/**
 * GET `/api/demo/livestock/animals/{id}/status-history` — ef-041-5's own single API
 * addition, the animal detail screen's own audit trail. MOST RECENT FIRST (the server's
 * own ordering — see `AnimalStatusHistoryRepository.GetByAnimalIdAsync`). Mirrors
 * `AnimalStatusChangeResponse` field-for-field.
 */
export interface AnimalStatusChange {
  id: Guid;
  animalId: Guid;
  fromStatus: AnimalStatus;
  toStatus: AnimalStatus;
  reason: string | null;
  changedBy: Guid;
  changedAt: string;
  /** ef-059 — `YYYY-MM-DD`; always present once ef-059-3 lands. */
  effectiveOn: string;
}

// ---- ef-059 anchor (frozen): dated weights, bulk entry, calf crop, head count ----------

/** `POST /api/demo/livestock/animals/{id}/weights`. `weighedOn` omitted = today. */
export interface AddAnimalWeightRequest {
  weighedOn?: string | null;
  weightLb: number;
}

/** One recorded weighing — `GET .../animals/{id}/weights` (newest `weighedOn` first). */
export interface AnimalWeight {
  id: Guid;
  animalId: Guid;
  weighedOn: string;
  weightLb: number;
  createdAt: string;
}

/** One row of `POST /api/demo/livestock/animals/bulk` (herd/group come from the request). */
export interface BulkAnimalRow {
  tag: string;
  name?: string | null;
  sex?: string | null;
  breed?: string | null;
  birthDate?: string | null;
  weightCurrent?: number | null;
  brand?: string | null;
  color?: string | null;
  ownerName?: string | null;
  notes?: string | null;
  twin?: boolean | null;
  damId?: Guid | null;
  sireId?: Guid | null;
}

/** `POST /api/demo/livestock/animals/bulk` — 1 to 200 rows, all or nothing. A rejected row is
 *  a 400 whose `errors` keys read `animals[<zero-based row>].<field>`. */
export interface BulkCreateAnimalsRequest {
  entityId: Guid;
  herdId?: Guid | null;
  groupId?: Guid | null;
  animals: BulkAnimalRow[];
}

export interface BulkCreateAnimalsResponse {
  count: number;
  animals: Animal[];
}

/** One calf of a `POST /api/demo/livestock/calf-crop`; `birthWeight` (lb) is recorded as a
 *  weighing dated the BIRTH date. */
export interface CalfRow {
  tag: string;
  name?: string | null;
  sex?: string | null;
  birthWeight?: number | null;
  color?: string | null;
  brand?: string | null;
}

/** `POST /api/demo/livestock/calf-crop` — 1 to 4 calves, one dam, one day. A rejected calf is
 *  a 400 whose `errors` keys read `calves[<zero-based row>].<field>`. */
export interface CalfCropRequest {
  damId: Guid;
  sireId?: Guid | null;
  birthDate: string;
  herdId?: Guid | null;
  groupId?: Guid | null;
  calves: CalfRow[];
}

export interface CalfCropResponse {
  damId: Guid;
  birthDate: string;
  calves: Animal[];
}

/** `GET /api/demo/livestock/headcount` — head ALIVE (active, already born) in the herd/group
 *  on `asOf`. */
export interface LivestockHeadcount {
  herdId: Guid | null;
  groupId: Guid | null;
  asOf: string;
  count: number;
}

/**
 * ef-041-6 — one line of a `POST /api/demo/livestock/purchase` batch. Exactly one of
 * `existingAnimalId`/`tag` is set, never both and never neither (`ValidatePurchaseLines`
 * in `LivestockEndpoints.cs`) — a brand-new animal, or a reference to one already on file,
 * each at its own `pricePerHead`. Mirrors `PurchaseAnimalLine` field-for-field.
 */
export interface PurchaseAnimalLine {
  existingAnimalId?: Guid | null;
  tag?: string | null;
  pricePerHead: number;
}

/**
 * `POST /api/demo/livestock/purchase`. `animals` must be non-empty. One balanced `entry`
 * posts for the WHOLE batch (amount = the sum of every line's `pricePerHead`), plus one
 * `animal_transaction` row per line. Mirrors `CreatePurchaseRequest` field-for-field.
 */
export interface CreatePurchaseRequest {
  entityId: Guid;
  animals: PurchaseAnimalLine[];
}

/**
 * ef-041-6 — one line of a `POST /api/demo/livestock/sale` batch: an existing, currently
 * `active` animal and its own sale price. Mirrors `SaleAnimalLine` field-for-field.
 */
export interface SaleAnimalLine {
  animalId: Guid;
  pricePerHead: number;
}

/**
 * `POST /api/demo/livestock/sale`. Every named animal must resolve on this tenant AND be
 * `active` — a bad line rejects the WHOLE batch before any write (409, nothing posted).
 * Mirrors `CreateSaleRequest` field-for-field.
 */
export interface CreateSaleRequest {
  entityId: Guid;
  animals: SaleAnimalLine[];
}

/**
 * Response for both `POST .../purchase` and `POST .../sale` — the one ledger entry the
 * batch produced. Mirrors `LivestockPostingResponse` field-for-field.
 *
 * ⚠️ Carries NO `animalIds` member — `LivestockContracts.cs`'s own `LivestockPostingResponse`
 * only has `EntryId`/`EntryAmount`. `livestock-ledger.spec.ts`'s purchase test reads a
 * `body.animalIds` array off the real response, which this contract cannot produce; flagged
 * in this job's own return contract (Blockers) rather than shaping this type to a field the
 * server does not send.
 */
export interface LivestockPostingResponse {
  entryId: Guid;
  entryAmount: number;
}

// ---- ef-045-3: fields ------------------------------------------------------------
// GET/POST/PATCH `/api/demo/farm/fields`, `/api/demo/farm/field-crops` — ef-045-2's
// routes (`FieldEndpoints.cs`/`FieldContracts.cs`). Additive only — nothing above this
// section changes. NO archive/restore route exists on either table this wave (0059's
// `archived_at` column has no route that sets it yet — `FieldResponse`'s own remarks in
// `FieldContracts.cs`, and `fields.spec.ts`'s own header comment says the same); this
// file carries no `archivedAt`-setting request type for that reason.

/**
 * GET/POST/PATCH `/api/demo/farm/fields` — one field, the owner's own unit of land.
 * Mirrors `FieldResponse` field-for-field.
 */
export interface Field {
  id: Guid;
  entityId: Guid;
  name: string;
  /** The field's own total acreage — optional until measured or entered, and never
   *  derived from any `FieldCrop.acres` (a field can carry more planted acres across its
   *  crops than its own measured size, or less, or none at all). */
  acres: number | null;
  /** Always `null` this wave — see this section's own header remarks. Carried on the
   *  wire now so a future archive/restore slice is additive, never breaking. */
  archivedAt: string | null;
  createdAt: string;
}

/** `POST /api/demo/farm/fields`. `acres` is optional but, when supplied, must be
 *  strictly positive (`FieldEndpoints.ValidateFieldAcres`). */
export interface CreateFieldRequest {
  entityId: Guid;
  name: string;
  acres?: number;
}

/** `PATCH /api/demo/farm/fields/{id}`. Every field optional — whatever is omitted keeps
 *  its current value (`FieldRepository.UpdateFieldAsync`'s COALESCE update), same
 *  partial-update convention as `UpdateAnimalRequest`. */
export interface UpdateFieldRequest {
  name?: string;
  acres?: number;
}

/**
 * GET/POST/PATCH `/api/demo/farm/field-crops` — a crop planted on one field, one year.
 * More than one can exist for the same field+year (double-cropping) — only the exact
 * same (field, year, crop) triple is refused. Mirrors `FieldCropResponse` field-for-field.
 */
export interface FieldCrop {
  id: Guid;
  entityId: Guid;
  fieldId: Guid;
  year: number;
  crop: string;
  /** Planted acres for THIS crop, THIS year — distinct from, and never derived from,
   *  `Field.acres`. */
  acres: number;
  createdAt: string;
}

/** `POST /api/demo/farm/field-crops`. `fieldId` must resolve on this tenant — a
 *  cross-tenant or nonexistent id is a 404, never a validation problem (`FieldEndpoints.cs`'s
 *  own remarks). A 409 means the exact same (field, year, crop) already exists; a
 *  different crop the same field+year (double-cropping) is accepted. */
export interface CreateFieldCropRequest {
  fieldId: Guid;
  year: number;
  crop: string;
  acres: number;
}

/** `PATCH /api/demo/farm/field-crops/{id}`. Only `acres` is patchable — `fieldId`/
 *  `year`/`crop` together are the row's own natural key (0059's UNIQUE), so changing any
 *  of them would be a new crop-year record, not an edit of this one. */
export interface UpdateFieldCropRequest {
  acres?: number;
}

// ---- ef-045-4: applications -------------------------------------------------------
// GET/POST `/api/demo/farm/field-crops/{id}/applications` — ef-045-4's routes
// (`FieldApplicationEndpoints.cs`/`FieldApplicationContracts.cs`). Add-only this wave —
// no PATCH/DELETE route exists (`FieldApplicationEndpoints.cs`'s own header comment), so
// no update request type is carried here for that reason, same posture the `ef-045-3`
// section above takes for its own missing archive/restore route.

export type FieldApplicationType = "chemical" | "fertilizer" | "seed";

/**
 * GET/POST `/api/demo/farm/field-crops/{id}/applications` — one chemical/fertilizer/
 * seed application logged against a field-crop. Mirrors `FieldApplicationResponse`
 * field-for-field. NEVER posts to the ledger — `cost`, when present, is a record only
 * (D4 — see `FieldApplicationEndpoints.cs`'s own remarks); there is no `entryId` here.
 */
export interface FieldCropApplication {
  id: Guid;
  entityId: Guid;
  fieldCropId: Guid;
  applicationType: FieldApplicationType;
  product: string;
  /** Optional — `rate`/`rateUnit` travel together but neither implies the other is set
   *  (`FieldApplicationContracts.cs`'s own `Rate`/`RateUnit`). */
  rate: number | null;
  rateUnit: string | null;
  appliedOn: string;
  /** Never posted to the ledger — see this section's own header comment. */
  cost: number | null;
  createdAt: string;
}

/** `POST /api/demo/farm/field-crops/{id}/applications`. No `fieldCropId`/`entityId`
 *  member on purpose — both come from the path and the field-crop it resolves to, never
 *  the body (`CreateFieldApplicationRequest`'s own remarks in `FieldApplicationContracts.cs`).
 *  The wire field is `unit`, not `rateUnit` — the response renames it on the way out;
 *  see `FieldApplicationEndpoints.cs`'s `ValidateUnit`. */
export interface CreateFieldApplicationRequest {
  applicationType: FieldApplicationType;
  product: string;
  rate?: number;
  unit?: string;
  appliedOn: string;
  cost?: number;
}

// ---- ef-045-5: harvest ------------------------------------------------------------
// POST/GET `/api/demo/farm/field-crops/{id}/harvest(s)` — ef-045-5a's routes
// (`FieldHarvestEndpoints.cs`/`FieldHarvestContracts.cs`). Add-only this wave — no
// PATCH/DELETE route exists, same posture the `ef-045-4` section above takes for its own
// missing update/delete routes.

/** Units a harvest can be logged in — `FieldHarvestEndpoints.AllowedUnits`, wire values
 *  lowercase and case-sensitive. */
export type FieldHarvestUnit = "bu" | "ton" | "cwt";

/**
 * GET/POST `/api/demo/farm/field-crops/{id}/harvest(s)` — one harvest event against a
 * field-crop; posting one also posts a balanced ledger entry (`source = 'crop'`) against
 * the tenant's `4010 Grain Sales` account through the pre-existing two-line shim. Mirrors
 * `FieldHarvestResponse` field-for-field.
 *
 * NO `yield` property — deliberately (D6). Yield (`qty / acres`) is computed
 * CLIENT-SIDE from this record's own `qty` and the owning field-crop's own `acres`
 * (already known from `GET .../field-crops`), never stored or returned by the server —
 * see `FieldHarvestResponse`'s own remarks in `FieldHarvestContracts.cs`.
 */
export interface FieldCropHarvest {
  id: Guid;
  entityId: Guid;
  fieldCropId: Guid;
  harvestedOn: string;
  qty: number;
  unit: FieldHarvestUnit;
  /** Set only on the SOLD branch (no `binId`); null on the STORED branch — the nullable
   *  pair this field ships with widened field-for-field from `FieldHarvestResponse.cs`
   *  (ef-046-1b, already merged; this section's own types predate that merge). */
  pricePerUnit: number | null;
  /** The balanced ledger entry this harvest's sale posted (`source = 'crop'`) — set only
   *  on the SOLD branch, null on the STORED branch. */
  entryId: Guid | null;
  /** The posted entry's own amount — read back from `entry` server-side, never
   *  recomputed client-side (`qty * pricePerUnit` rounded is a close but not
   *  authoritative echo). Set only on the SOLD branch, null on the STORED branch. */
  entryAmount: number | null;
  /** The bin this harvest went into — set only on the STORED branch (ef-046-1b), null on
   *  the SOLD branch. */
  binId: Guid | null;
  /** ef-057-1a — the grain contract this harvest was delivered straight to, bypassing every
   *  bin. Set only on the DELIVERED branch; null on SOLD and STORED. On a DELIVERED
   *  harvest `entryId`/`entryAmount` are null — the money lives on the delivery
   *  (`deliveryEntryId`/`deliveryDocumentId`/`deliveryAmount`), same as a bin delivery. */
  contractId: Guid | null;
  /** ef-057-1a — qty times the price used; DELIVERED branch only, paid or invoiced. */
  deliveryAmount: number | null;
  /** ef-057-1a — the entry a PAID direct delivery posted; null when unpaid. */
  deliveryEntryId: Guid | null;
  /** ef-057-1a — the invoice document an UNPAID direct delivery posted; null when paid. */
  deliveryDocumentId: Guid | null;
  createdAt: string;
}

/** `POST /api/demo/farm/field-crops/{id}/harvest`. SOLD XOR STORED (ef-046-1b): omit
 *  `binId` and supply `pricePerUnit` to record a sale — the unchanged ef-045-5a path — or
 *  supply `binId` to record a stored harvest instead, with no entry posted. `harvestedOn`/
 *  `qty`/`unit` are required either way (`CreateFieldHarvestRequest`'s own remarks in
 *  `FieldHarvestContracts.cs`). */
export interface CreateFieldHarvestRequest {
  harvestedOn: string;
  qty: number;
  unit: FieldHarvestUnit;
  pricePerUnit?: number;
  /** The bin this harvest goes into — omit to sell (the unchanged ef-045-5a path), supply
   *  to store (ef-046-1c's own bin picker in Fields.tsx). Mutually exclusive with
   *  `pricePerUnit`. */
  binId?: Guid;
  /** ef-057-1a — deliver this harvest straight to a grain contract, bypassing every bin.
   *  Mutually exclusive with `binId` (400 when both). `pricePerUnit`, when supplied,
   *  overrides the contract's own price for this delivery; omit it to use the contract's. */
  deliverTo?: FieldHarvestDeliverTo;
}

/** ef-057-1a — `deliverTo` on `CreateFieldHarvestRequest`. Both fields required; `paid`
 *  has no default (same as `CreateGrainDeliveryRequest`). */
export interface FieldHarvestDeliverTo {
  contractId: Guid;
  paid: boolean;
}

// ---- ef-063 anchors: scale loads ---------------------------------------------------------
// POST/GET `/api/demo/farm/grain-loads`, POST `/api/demo/farm/grain-loads/import` (`GrainLoadEndpoints.cs` /
// `GrainLoadImportEndpoints.cs` / `GrainLoadContracts.cs`). A load is one scale ticket: grain hauled off a
// field crop to ONE destination (a bin, farm or elevator, or straight to a contract); an accepted load always
// has one `grain_movement`. Quantities are in the load's own `unit`; a ticket is identified by (source, ticketNo).

/** `POST /api/demo/farm/grain-loads`. Exactly one of `binId` / `deliverTo` (400 naming `destination`). `net` must
 *  equal `gross - tare` exactly (400 naming `net`). A duplicate (`manual`, `ticketNo`) is a 409. */
export interface CreateGrainLoadRequest {
  ticketNo: string;
  /** ISO date-time WITH an offset, e.g. `2026-10-02T14:30:00-05:00`. */
  weighedAt: string;
  fieldCropId: Guid;
  unit: GrainUnit;
  gross: number;
  tare: number;
  net: number;
  moisture?: number;
  testWeight?: number;
  dockage?: number;
  truck?: string;
  driver?: string;
  binId?: Guid;
  deliverTo?: FieldHarvestDeliverTo;
  pricePerUnit?: number;
}

export interface GrainLoad {
  id: Guid;
  entityId: Guid;
  source: string;
  ticketNo: string;
  weighedAt: string;
  fieldCropId: Guid | null;
  crop: string;
  unit: GrainUnit;
  gross: number;
  tare: number;
  net: number;
  moisture: number | null;
  testWeight: number | null;
  dockage: number | null;
  truck: string | null;
  driver: string | null;
  /** The destination bin (null when delivered straight to a contract). */
  binId: Guid | null;
  /** The destination contract (null when the load went into a bin). */
  contractId: Guid | null;
  /** The one `grain_movement` this load created. */
  movementId: Guid | null;
  createdAt: string;
}

/** `POST .../grain-loads/import` 200. A re-import of the same file answers `created: 0`. */
export interface GrainLoadImportResult {
  source: string;
  received: number;
  created: number;
  skipped: number;
}

/** One bad row of a refused import (the 400 body's `rowErrors`; nothing was imported). `line` is the 1-based CSV
 *  file line (header = 1) or the 1-based array position for JSON. */
export interface GrainLoadRowError {
  line: number;
  field: string;
  message: string;
}

// ---- ef-064-12..15 anchors: scale aliases + the Ag Scale ticket import -------------------------------------------
// Mirrors `ScaleAliasContracts.cs` (FROZEN). An alias says "this name on a scale ticket means that field / bin / commodity".

/** The three kinds of ticket name an alias can translate: her `origin` (field), `destination` (bin), `commodity`. */
export type ScaleAliasKind = "field" | "bin" | "commodity";

export interface ScaleAlias {
  id: Guid;
  source: string;
  kind: ScaleAliasKind;
  text: string;
  targetId: Guid;
  /** The target row's current name. */
  targetName: string;
}

export interface CreateScaleAliasRequest {
  /** Absent = `agscale`. */
  source?: string;
  kind: ScaleAliasKind;
  text: string;
  targetId: Guid;
}

/** One item of `POST /api/demo/farm/scale-aliases/batch` (the source is the batch's). */
export type ScaleAliasBatchItem = Pick<CreateScaleAliasRequest, "kind" | "text" | "targetId">;

/** Body of the batch save: 1 to 100 items, one source for all. */
export interface ScaleAliasBatchRequest {
  /** Absent = `agscale`. */
  source?: string;
  aliases: ScaleAliasBatchItem[];
}

export interface ScaleAliasBatchResponse {
  created: number;
  retargeted: number;
  aliases: ScaleAlias[];
}

/** The most items one batch save may carry. */
export const SCALE_ALIAS_MAX_BATCH = 100;

/** One name a refused `format=agscale` import could not match (the 400 body's `unmapped`). */
export interface ScaleAliasUnmapped {
  kind: ScaleAliasKind;
  text: string;
  lines: number[];
}

/** Which reader the import uses: Christy's Ag Scale ticket app (her JSON or her CSV export) or EasyFarm's own columns. */
export type GrainLoadImportFormat = "agscale" | "canonical";

/** A refused import, parsed back out of the ApiError (`importRefusal` in client.ts). Nothing was imported. */
export interface GrainLoadImportRefusal {
  /** A whole-file problem (`errors.file`, `errors.format`, `errors.source`, `errors.entityId`), or null for row problems. */
  fileError: string | null;
  rowErrors: GrainLoadRowError[];
  unmapped: ScaleAliasUnmapped[];
}

/** GET /api/demo/farm/commodities - one row of the farm's effective catalog (ef-064-2, `CommodityContracts.cs`). */
export interface Commodity {
  id: Guid;
  name: string;
  defaultUnit: string;
  lbPerBu: number | null;
  customised: boolean;
}

// ---- ef-046-5: overview ------------------------------------------------------------
// GET `/api/demo/farm/overview` — the Farm tile's Overview tab (`FarmOverviewEndpoints.cs`/
// `FarmOverviewContracts.cs`). Four whole-farm aggregates, read-only — mirrors
// `FarmOverviewResponse` field-for-field.

/** One (crop, qty) balance inside `FarmOverview.bushelsOnHand` — summed across every bin
 *  for the crop, not per bin (`FarmOverviewRepository.GetBushelsOnHandAsync`'s own
 *  remarks). NO `unit` property — the server sums raw `qty` per crop regardless of the
 *  unit each movement was recorded in (bu/ton/cwt), so this figure is a farm-wide count,
 *  not a unit-labeled measurement. */
export interface FarmOverviewBushels {
  crop: string;
  qty: number;
}

/** `GET /api/demo/farm/overview` — the Farm tile's Overview tab. Every figure is a
 *  whole-farm aggregate computed at read time, never stored (`FarmOverviewResponse`'s own
 *  remarks in `FarmOverviewContracts.cs`). */
export interface FarmOverview {
  /** Sum of `field_crop.acres` for the current year, STILL STANDING — a field-crop with
   *  a harvest already recorded against it has come out of this figure
   *  (`FarmOverviewRepository.GetAcresInCropAsync`'s own remarks). */
  acresInCrop: number;
  /** Count of active animals. */
  headCount: number;
  bushelsOnHand: FarmOverviewBushels[];
  /** Sum, across every forward-sale contract, of `(qty - delivered) * pricePerUnit` — a
   *  fulfilled contract contributes zero, never negative. */
  openContractBalance: number;
  /** Milk shipped, summed by unit — a LIFETIME total (every ticket ever recorded, any
   *  `paid` status — "milk shipped to date," not "milk cash received", ef-049-2), never
   *  summed across the two incompatible units (gal/cwt kept separate). */
  milkByUnit: FarmOverviewMilk[];
}

/** One (unit, qty) balance inside `FarmOverview.milkByUnit` — see `FarmOverview.milkByUnit`'s
 *  own remarks. Mirrors `FarmOverviewMilkResponse` field-for-field. */
export interface FarmOverviewMilk {
  unit: MilkUnit;
  qty: number;
}

// ---- ef-046-1: bins ----------------------------------------------------------------
// GET/POST `/api/demo/farm/grain-bins` — ef-046-1b's routes (`GrainBinEndpoints.cs`/
// `GrainBinContracts.cs`). NO archive/restore route exists this wave (`archivedAt` is
// carried for parity, same posture `Field.archivedAt` takes above), so no such request
// type is carried here.

/** One (crop, on-hand qty) balance inside a `GrainBin` — derived server-side at READ
 *  time from `grain_movement`, never stored. Mirrors `GrainBinOnHandResponse`
 *  field-for-field. No `unit` member — `grain_bin`/`grain_movement`'s own schema keys
 *  on-hand by crop alone (`GrainBinOnHandResponse`'s own shape). */
export interface GrainBinOnHand {
  crop: string;
  qty: number;
}

/**
 * GET/POST `/api/demo/farm/grain-bins` — one physical grain bin. Mirrors
 * `GrainBinResponse` field-for-field.
 */
export interface GrainBin {
  id: Guid;
  entityId: Guid;
  name: string;
  /** Rated capacity — optional until measured or entered, same posture as
   *  `Field.acres`. Not stored with a unit (0063's own header) — never rendered
   *  alongside an assumed bu/ton/cwt suffix. */
  capacity: number | null;
  /** Always `null` this wave — no archive/restore route exists yet (see this
   *  section's own header remarks). */
  archivedAt: string | null;
  createdAt: string;
  /** This bin's own balance, one entry per crop it currently holds — derived at READ
   *  time, never stored. */
  onHand: GrainBinOnHand[];
  /** ef-063 anchor (0089) — `"farm"` (the default) or `"elevator"`: off-farm storage at `vendorName`. Grain held
   *  at an elevator is still the farm's, but it is not on the farm (the grain inventory report splits on this). */
  locationKind: GrainLocationKind;
  /** ef-063 anchor — the elevator's vendor; `null` on a farm bin, always set on an elevator bin. */
  vendorId: Guid | null;
  /** ef-063 anchor — the elevator vendor's current name; `null` on a farm bin. */
  vendorName: string | null;
}

/** ef-063 anchor — where a grain bin physically is (`grain_bin.location_kind`, 0089). */
export type GrainLocationKind = "farm" | "elevator";

/** `POST /api/demo/farm/grain-bins`. `capacity` is optional but, when supplied, must be
 *  strictly positive (`GrainBinEndpoints.ValidateCapacity`). `locationKind` defaults to `"farm"`;
 *  an `"elevator"` bin REQUIRES `vendorId` (400 naming `vendorId` otherwise) and a farm bin refuses it. */
export interface CreateGrainBinRequest {
  entityId: Guid;
  name: string;
  capacity?: number;
  locationKind?: GrainLocationKind;
  vendorId?: Guid;
}

// ---- ef-063 anchors: elevator storage fee ------------------------------------------------
// POST `/api/demo/farm/grain-bins/{id}/storage-fee` (`GrainStorageFeeEndpoints.cs` /
// `GrainStorageFeeContracts.cs`) — posts ONE bill (a `document`, kind `bill`) to the elevator's vendor.

/** `POST .../grain-bins/{id}/storage-fee`. 400: not an elevator bin / bad amount / account not an expense
 *  account / due before incurred; 404: the bin is not this tenant's. */
export interface CreateGrainStorageFeeRequest {
  amount: number;
  expenseAccountId: Guid;
  incurredOn?: string;
  dueOn?: string;
  memo?: string;
}

export interface GrainStorageFee {
  binId: Guid;
  document: Document;
}

// ---- ef-046-4: contracts and deliveries ---------------------------------------------
// POST/GET/PATCH `/api/demo/farm/grain-contracts`, POST `/api/demo/farm/grain-deliveries`
// — ef-046-2/-3's routes (`GrainContractEndpoints.cs`/`GrainContractContracts.cs`,
// `GrainDeliveryEndpoints.cs`/`GrainDeliveryContracts.cs`). `Delivered`/`Remaining`/
// `Status` on `GrainContract` are derived server-side, never stored — this client never
// recomputes them (`GrainContractResponse`'s own remarks).

/** Units a grain contract/delivery is struck in — `GrainContractEndpoints.AllowedUnits`,
 *  wire values lowercase and case-sensitive, the same closed set `FieldHarvestUnit`
 *  already uses. */
export type GrainUnit = "bu" | "ton" | "cwt";

/** `open` (nothing delivered yet), `partial` (some but not all), or `fulfilled`
 *  (delivered >= qty) — derived server-side from delivered qty vs qty, never stored. */
export type GrainContractStatus = "open" | "partial" | "fulfilled";

/**
 * GET/POST/PATCH `/api/demo/farm/grain-contracts` — one forward-sale contract with a
 * grain buyer (a `Vendor`, never a separate lookup table — `GrainContractEndpoints.cs`'s
 * own remarks). Mirrors `GrainContractResponse` field-for-field.
 */
export interface GrainContract {
  id: Guid;
  entityId: Guid;
  buyerId: Guid;
  crop: string;
  qty: number;
  pricePerUnit: number;
  unit: GrainUnit;
  /** Local-bid-vs-futures differential. May legitimately be negative. */
  basis: number | null;
  futuresMonth: string | null;
  deliveryStart: string;
  deliveryEnd: string;
  /** Sum of delivered quantity against this contract so far — derived, never stored. */
  delivered: number;
  /** `qty` minus `delivered` — derived, never stored. */
  remaining: number;
  /** Derived, never stored — see this section's own header remarks. */
  status: GrainContractStatus;
  createdAt: string;
}

/** `POST /api/demo/farm/grain-contracts`. `buyerId` must resolve to a vendor on this
 *  tenant — a cross-tenant or nonexistent id is a 404, never a validation problem
 *  (`GrainContractEndpoints.cs`'s own remarks, deliberately diverging from
 *  `DocumentEndpoints`'s own `counterpartyId`). */
export interface CreateGrainContractRequest {
  entityId: Guid;
  buyerId: Guid;
  crop: string;
  qty: number;
  pricePerUnit: number;
  unit: GrainUnit;
  basis?: number;
  futuresMonth?: string;
  deliveryStart: string;
  deliveryEnd: string;
}

/** `PATCH /api/demo/farm/grain-contracts/{id}`. Only price, basis, futures month and
 *  delivery end are patchable — buyer, crop, qty, unit, entity and delivery start are
 *  the contract's own identity fields and have no member here at all, same
 *  "no member = cannot be patched" shape `UpdateFieldCropRequest` uses. Not called from
 *  Grain.tsx this wave (no edit affordance in the brief) but wired for parity with the
 *  route's own existence, same posture `updateField` takes beside `Field`. */
export interface UpdateGrainContractRequest {
  pricePerUnit?: number;
  basis?: number;
  futuresMonth?: string;
  deliveryEnd?: string;
}

/**
 * `POST /api/demo/farm/grain-deliveries` — one delivery against a `GrainContract`, out of
 * a `GrainBin`, in the contract's own unit. Mirrors `GrainDeliveryResponse`
 * field-for-field.
 */
export interface GrainDelivery {
  id: Guid;
  binId: Guid;
  contractId: Guid;
  crop: string;
  qty: number;
  unit: GrainUnit;
  pricePerUnit: number;
  amount: number;
  paid: boolean;
  /** Set only when `paid` — the balanced entry this delivery posted. Null on an unpaid
   *  delivery (see `documentId`). */
  entryId: Guid | null;
  /** Set only when NOT `paid` — the invoice document this delivery posted. Null on a
   *  paid delivery (see `entryId`). */
  documentId: Guid | null;
  createdAt: string;
}

/** `POST /api/demo/farm/grain-deliveries`. `qty` is in the CONTRACT's own unit — crop/
 *  unit are never taken from this request, only from the resolved `contractId`. A 409
 *  means the bin doesn't hold enough of the contract's crop, or this tenant's chart of
 *  accounts is missing the Grain Sales account (`GrainDeliveryEndpoints.cs`'s own
 *  remarks) — surfaced via `problemMessage`, never guessed client-side. */
export interface CreateGrainDeliveryRequest {
  binId: Guid;
  contractId: Guid;
  qty: number;
  pricePerUnit: number;
  paid: boolean;
}

// ---- ef-049-2: milk tickets ----------------------------------------------------------
// GET/POST `/api/demo/farm/milk-tickets` — ef-049-2's routes (`MilkTicketEndpoints.cs`/
// `MilkTicketContracts.cs`). Same paid/unpaid two-outcome shape `CreateGrainDeliveryRequest`/
// `GrainDelivery` already take, minus grain's own bin/on-hand/contract machinery — this
// wave is tickets only (D2).

/** Units a milk ticket is priced in — `MilkTicketRepository.IsAllowedUnit`, wire values
 *  lowercase and case-sensitive, the same closed-set posture `GrainUnit` already takes. */
export type MilkUnit = "gal" | "cwt";

/**
 * GET/POST `/api/demo/farm/milk-tickets` — one milk sale record. Mirrors
 * `MilkTicketResponse` field-for-field.
 */
export interface MilkTicket {
  id: Guid;
  entityId: Guid;
  ticketDate: string;
  qty: number;
  unit: MilkUnit;
  pricePerUnit: number;
  amount: number;
  paid: boolean;
  /** Set only when `paid` — the balanced entry this ticket posted. Null on an unpaid
   *  ticket (see `documentId`). */
  entryId: Guid | null;
  /** Set only when NOT `paid` — the invoice document this ticket posted. Null on a paid
   *  ticket (see `entryId`). */
  documentId: Guid | null;
  createdAt: string;
}

/** `POST /api/demo/farm/milk-tickets`. `entityId` not resolving on this tenant is a
 *  `400`, field `entityId` (a create-time body reference, never a path-resource id —
 *  `MilkTicketEndpoints.cs`'s own remarks), surfaced via `problemFieldErrors`, never
 *  guessed client-side. A `409` means this tenant's chart of accounts is missing the
 *  Milk Sales account. */
export interface CreateMilkTicketRequest {
  entityId: Guid;
  ticketDate: string;
  qty: number;
  unit: MilkUnit;
  pricePerUnit: number;
  paid: boolean;
}

// ---- ef-050-2: partner splits ---------------------------------------------------------
// POST/GET/PATCH `/api/demo/farm/partner-splits` plus `.../archive`/`.../restore` —
// ef-050-2's routes (`PartnerSplitEndpoints.cs`/`PartnerSplitContracts.cs`). CRUD only —
// no posting integration, no cross-row 100% cap (D5/Q3, restated in
// db/migrations/0075_partner_split.sql's own header).

/**
 * GET/POST `/api/demo/farm/partner-splits` — one partner's percentage share of one
 * entity's proceeds. Mirrors `PartnerSplitResponse` field-for-field.
 */
export interface PartnerSplit {
  id: Guid;
  entityId: Guid;
  partnerName: string;
  /** 0 < percentage <= 100 (`partner_split_percentage_check`, 0075). No cross-row cap —
   *  two rows on the same entity may sum past 100%, an explicit non-goal this wave. */
  percentage: number;
  /** Null = active. Archive is NOT idempotent (a re-archive answers 404); restore is —
   *  same posture `BusinessEntity.archivedAt` already takes. */
  archivedAt: string | null;
  createdAt: string;
}

/** `POST /api/demo/farm/partner-splits`. `entityId` not resolving on this tenant is a
 *  `400`, field `entityId` (a create-time body reference, never a path-resource id —
 *  same posture `CreateMilkTicketRequest.entityId` already takes), surfaced via
 *  `problemFieldErrors`, never guessed client-side. */
export interface CreatePartnerSplitRequest {
  entityId: Guid;
  partnerName: string;
  percentage: number;
}

/** `PATCH /api/demo/farm/partner-splits/{id}`. Both fields optional — whatever is
 *  omitted keeps its current value, same convention as `UpdateJobCodeRequest`. A partial
 *  update, never a full replace. */
export interface UpdatePartnerSplitRequest {
  partnerName?: string;
  percentage?: number;
}

// ---- ef-047-2: entities ----

/** `POST /api/demo/entities` — `EntityAdminContracts.cs`'s `CreateEntityRequest`. The
 *  response is `BusinessEntity` above, the same shape `GET /entities` already returns —
 *  entity carries no field that serializes differently between read and write the way
 *  `Account.kind` does, so there is no separate write-result type here. */
export interface CreateEntityRequest {
  name: string;
}

/** `PATCH /api/demo/entities/{id}` — RENAME ONLY, the sole field the server route
 *  accepts (`EntityAdminContracts.cs`'s `UpdateEntityRequest`). `pay_schedule`/
 *  `firstPayday` stay on their own existing `PATCH .../pay-schedule` route. */
export interface UpdateEntityRequest {
  name: string;
}

// ---- ef-051-1: Home memos -------------------------------------------------------------
// POST/GET/PATCH `/api/demo/home/memos` plus `.../archive` — ef-051-1's routes
// (`MemoEndpoints.cs`/`MemoContracts.cs`). No restore route this slice (explicit scope
// cut, same posture the server contract's own header records).

/** GET/POST `/api/demo/home/memos` — one short, dated-or-undated note. Mirrors
 *  `MemoResponse` field-for-field. */
export interface Memo {
  id: Guid;
  entityId: Guid | null;
  /** `yyyy-MM-dd`, or null for an undated running note — an ongoing note with no
   *  particular day, sorted last by the server (`memo_date IS NULL ASC, memo_date ASC,
   *  created_at ASC`). Never re-sorted or re-filtered client-side. */
  memoDate: string | null;
  body: string;
  /** Null = active. Archive is NOT idempotent (a re-archive answers 404) — no restore
   *  route this slice. */
  archivedAt: string | null;
  createdAt: string;
}

/** `POST /api/demo/home/memos`. `entityId`, when present and not resolving on this
 *  tenant, answers `400` field `entityId` (a create-time body reference, never a
 *  path-resource id — same posture `CreatePartnerSplitRequest.entityId` already takes).
 *  The Home card's inline form never sends one — no entity picker this slice.
 *  `memoDate` is unbounded in the past and accepted at most one day ahead of today. */
export interface CreateMemoRequest {
  entityId?: Guid;
  /** `yyyy-MM-dd`. Omitted = undated. */
  memoDate?: string;
  body: string;
}

// ---- ef-060 anchor: piece pay (an extra earnings line beside hourly pay) ------------------

/** A kind of piece work paid by the unit — `GET/POST /api/demo/piece-types`. */
export interface PieceType {
  id: Guid;
  name: string;
  /** What one piece is ("head", "lb"). */
  unit: string;
  /** Dollars per unit, up to 4 decimals. */
  rate: number;
  herdId?: Guid | null;
  herdName?: string | null;
  groupId?: Guid | null;
  groupName?: string | null;
  /** Null while in use; archived types are hidden from pickers (never deleted). */
  archivedAt?: string | null;
  createdAt: string;
}

export interface CreatePieceTypeRequest {
  name: string;
  /** Omitted -> "head". */
  unit?: string;
  rate: number;
  herdId?: Guid;
  groupId?: Guid;
}

export interface UpdatePieceTypeRequest {
  name?: string;
  unit?: string;
  rate?: number;
  herdId?: Guid;
  groupId?: Guid;
  unlinkLivestock?: boolean;
}

export type PieceTicketSource = "manual" | "livestock";
export type PieceTicketStatus = "open" | "paid" | "void";

/** One piece ticket — units, rate, amount and as-of date are snapshots. */
export interface PieceTicket {
  id: Guid;
  employeeId: Guid;
  employeeName: string;
  pieceTypeId: Guid;
  pieceTypeName: string;
  unit: string;
  source: PieceTicketSource;
  units: number;
  rate: number;
  /** round(units x rate, 2) — fixed once the ticket is paid. */
  amount: number;
  asOf?: string | null;
  periodEnd: string;
  note?: string | null;
  status: PieceTicketStatus;
  payRunId?: Guid | null;
  payRunLineId?: Guid | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  createdAt: string;
}

export interface CreatePieceTicketRequest {
  employeeId: Guid;
  pieceTypeId: Guid;
  /** Default "manual". "livestock" pulls the head count as of `asOf` (units must be omitted). */
  source?: PieceTicketSource;
  units?: number;
  asOf?: string;
  /** Default: asOf, else today. */
  periodEnd?: string;
  note?: string;
}

export interface UpdatePieceTicketRequest {
  units?: number;
  periodEnd?: string;
  note?: string;
}

/** `GET /api/demo/piece-tickets` query. */
export interface PieceTicketQuery {
  employeeId?: Guid;
  status?: PieceTicketStatus;
  /** 1-500, default 100. Newest first. */
  limit?: number;
}

export interface PieceRunEmployee {
  employeeId: Guid;
  employeeNumber: string;
  employeeName: string;
  ticketCount: number;
  amount: number;
  /** False for a piece-only worker whose line finalize will add. */
  hasLine: boolean;
}

/** `GET /api/demo/payroll/runs/{id}/piece` — the tickets behind a pay run. */
export interface PieceRunSummary {
  payRunId: Guid;
  finalized: boolean;
  voided: boolean;
  employees: PieceRunEmployee[];
  tickets: PieceTicket[];
  total: number;
}

// ---- ef-061 anchor: configurable Home tiles, equipment, reminders -------------------------

/** One Home tile as the picker names it — `GET/PUT/DELETE /api/demo/home/layout`. */
export interface HomeTileRef {
  id: string;
  title: string;
}

/** The caller's own Home tiles, in order (permission-filtered), plus what they could still add. */
export interface HomeLayout {
  tiles: HomeTileRef[];
  available: HomeTileRef[];
  /** True while this login has never saved a layout (or reset it). */
  isDefault: boolean;
}

export interface BillsDueBucket {
  count: number;
  /** Outstanding dollars, 2 decimals; 0 when `count` is 0. */
  total: number;
}

/** `GET /api/demo/home/bills-due` — unpaid bills due within `windowDays` (30), and overdue ones. */
export interface BillsDue {
  entityId: Guid;
  asOf: string;
  windowDays: number;
  due: BillsDueBucket;
  overdue: BillsDueBucket;
}

export interface Equipment {
  id: Guid;
  name: string;
  makeModel?: string | null;
  serialNumber?: string | null;
  notes?: string | null;
  /** Hours of the latest-dated reading; null until one is logged. */
  currentHours?: number | null;
  lastReadingOn?: string | null;
  archivedAt?: string | null;
  createdAt: string;
}

export interface CreateEquipmentRequest {
  name: string;
  makeModel?: string;
  serialNumber?: string;
  notes?: string;
}

/** Partial; a whitespace-only text value clears it. */
export interface UpdateEquipmentRequest {
  name?: string;
  makeModel?: string;
  serialNumber?: string;
  notes?: string;
}

export interface LogHoursRequest {
  hours: number;
  /** Default today; not in the future. */
  readOn?: string;
  /** Required when the reading is lower than the previous one or higher than the next one. */
  note?: string;
}

export interface HoursReading {
  id: Guid;
  equipmentId: Guid;
  hours: number;
  readOn: string;
  note?: string | null;
  createdAt: string;
}

export type ReminderState = "due" | "soon" | "later" | "done";

export interface Reminder {
  id: Guid;
  title: string;
  notes?: string | null;
  equipmentId?: Guid | null;
  equipmentName?: string | null;
  dueOn?: string | null;
  dueHours?: number | null;
  intervalDays?: number | null;
  intervalHours?: number | null;
  /** Computed on every read: due / soon (14 days or 25 hours) / later / done. */
  state: ReminderState;
  /** Negative when overdue; null without a date trigger. */
  daysUntilDue?: number | null;
  /** Negative when past; null without an hours trigger or a reading. */
  hoursUntilDue?: number | null;
  currentHours?: number | null;
  lastDoneOn?: string | null;
  lastDoneHours?: number | null;
  doneAt?: string | null;
  archivedAt?: string | null;
  createdAt: string;
}

export interface CreateReminderRequest {
  title: string;
  notes?: string;
  equipmentId?: Guid;
  dueOn?: string;
  dueHours?: number;
  intervalDays?: number;
  intervalHours?: number;
}

export interface UpdateReminderRequest {
  title?: string;
  notes?: string;
  dueOn?: string;
  dueHours?: number;
  intervalDays?: number;
  intervalHours?: number;
}

export interface CompleteReminderRequest {
  /** Default today. */
  doneOn?: string;
  /** Default the equipment's current hours. */
  hoursAtDone?: number;
  note?: string;
}

/** `GET /api/demo/reminders/due-soon` — the Home tile's read. Counts cover ALL; `items` is the first `limit`. */
export interface DueSoonReminders {
  dueCount: number;
  soonCount: number;
  items: Reminder[];
}

// ---- ef-062-8 anchor: starting check number ------------------------------------------------

/** `GET|PUT /api/demo/entities/{id}/next-check-number` (`NextCheckNumberContracts.cs`). `nextCheckNumber` is exactly what
 *  the shared per-entity allocator would hand out now; `highestOnRecord` is the highest check number the entity has ever
 *  used (payroll or register, voided included), or null when it has none. */
export interface NextCheckNumber {
  entityId: Guid;
  nextCheckNumber: number;
  highestOnRecord: number | null;
}


// ---- ef-064-17 / 64-18 anchors: a payee's tax identity (EXACT mirror of VendorTaxContracts.cs) ----------------------------------

/** `nec` reports the vendor on a 1099-NEC (the default); `exempt` never does (a corporation, card payments). */
export type Form1099 = "nec" | "exempt";

export type TinType = "ssn" | "ein" | "itin";

/** `ready` (tax ID + address on file), `missing` (see `VendorTax.missing`) or `exempt`. */
export type VendorTaxStatus = "ready" | "missing" | "exempt";

/** What a read may say about a tax ID: that one is on file, its kind, its last four, its mask. NEVER the number. */
export interface VendorTinState {
  hasTin: boolean;
  tinType: TinType | null;
  tinLast4: string | null;
  masked: string | null;
}

/** `GET /api/demo/vendors/{id}/tax` and the 200 of every write below. */
export interface VendorTax {
  vendorId: Guid;
  name: string;
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  form1099: Form1099;
  tin: VendorTinState;
  status: VendorTaxStatus;
  /** Empty unless `status` is `missing`; `taxId` then `address`. */
  missing: ("taxId" | "address")[];
}

/** `GET /api/demo/vendors/tax` - every vendor of the tenant, by name. */
export interface VendorTaxList {
  vendors: VendorTax[];
}

/** `PUT /api/demo/vendors/{id}/tax` - the W-9 profile, replaced as a whole (a blank text field becomes null; `form1099` required). */
export interface PutVendorTaxRequest {
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  form1099: Form1099;
}

/** `PUT /api/demo/vendors/{id}/tax-id` - the only way a tax ID enters. The number is in this body and nowhere else, ever. */
export interface PutVendorTaxIdRequest {
  tinType: TinType;
  tin: string;
}

// ---- ef-064-22: the payer's (entity's) tax identity (mirror of EntityTaxContracts.cs) -------------------------------------------

/** `GET /api/demo/entities/{id}/tax` and the 200 of every write. Never carries the number, only its mask. */
export interface EntityTax {
  entityId: Guid;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  addressState: string | null;
  zip: string | null;
  tin: VendorTinState;
  /** A pre-64-22 plaintext number exists that has not been copied into the protected columns yet. */
  legacyPending: boolean;
  status: "ready" | "missing";
  missing: ("taxId" | "address")[];
}

export interface EntityTaxList {
  entities: EntityTax[];
}

export interface PutEntityTaxRequest {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  addressState: string | null;
  zip: string | null;
}

// ---- ef-064-22: the payer's (entity's) tax identity (mirror of EntityTaxContracts.cs) -------------------------------------------

/** `GET /api/demo/entities/{id}/tax` and the 200 of every write. Never carries the number, only its mask. */
export interface EntityTax {
  entityId: Guid;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  addressState: string | null;
  zip: string | null;
  tin: VendorTinState;
  /** A pre-64-22 plaintext number exists that has not been copied into the protected columns yet. */
  legacyPending: boolean;
  status: "ready" | "missing";
  missing: ("taxId" | "address")[];
}

export interface EntityTaxList {
  entities: EntityTax[];
}

export interface PutEntityTaxRequest {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  addressState: string | null;
  zip: string | null;
}
